class BundleBuilder extends HTMLElement {
  static documentListenersInitialized = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) return;

    // Check if this element has already been initialized by checking for a data attribute
    if (this.hasAttribute("data-initialized")) {
      return;
    }

    this.init();
  }

  init() {
    this.initialized = true;
    // Set data attribute to persist across browser back/forward navigation
    this.setAttribute("data-initialized", "true");

    // Initialize request queue for sequential API calls
    this.requestQueue = [];
    this.isProcessingQueue = false;

    // Global debounce timer for batching ALL quantity changes across all products
    this.globalQuantityDebounceTimer = null;

    this.bundleId = this.generateUniqueBundleId();
    this.bundleOptionsGrid = this.querySelector(".bundle-options-grid");
    this.bundleSummary = this.querySelector("[data-bundle-summary]");
    this.bundleSummaryFooter = this.querySelector(".bundle-summary__footer");
    this.bundleSummaryToggleContainer = this.querySelector(
      "[data-bundle-summary-toggle-container]"
    );
    this.bundleSummaryToggle = this.querySelector(
      "[data-bundle-summary-toggle]"
    );
    this.bundleQtySelectors = this.querySelectorAll("[data-qty-selector]");
    this.closeSummary = this.querySelectorAll("[data-close-summary]");

    // Initialize selling plan from localStorage if available, otherwise default to one-time
    const savedSellingPlan = localStorage.getItem("bundle-selling-plan");
    this.sellingPlan = savedSellingPlan || "one-time";

    this.sellingPlanInputs = this.querySelectorAll("[data-selling-plan]");

    // Apply the saved (or default) selling plan to the radio buttons
    this.applySellingPlanSelection(this.sellingPlan);
    this.selectedDiscountOption = this.querySelector(
      "[data-pack-option]:checked"
    );

    // Disable action buttons during initialization
    this.disableBundleSummaryActions();

    // Initialize debounced quantity change system
    this.quantityDebounceTimers = new Map(); // variantId -> timeout ID
    this.pendingQuantityUpdates = new Map(); // variantId -> final quantity to sync

    // No more localStorage - work directly with Shopify cart
    this.cartItems = []; // Will be synced with Shopify cart

    // Use event delegation for add to bundle buttons so they work after filter changes
    this.addEventListener("click", (event) => {
      const addButton = event.target.closest("[data-bundle-add]");
      if (addButton) {
        event.preventDefault();
        this.handleAddToBundle(event);
      }
    });

    this.selectedPack = this.querySelector("[data-pack-option]:checked");

    this.itemsContainer = this.querySelector("[data-items]");
    this.packOption = this.querySelectorAll("[data-pack-option]");
    this.defaultPackOption = this.querySelector(
      "[data-pack-option][data-default]"
    );
    this.discountAmount =
      parseFloat(this.selectedDiscountOption?.dataset.discount) || 0;
    this.isBundleMet = false;
    this.disabledCheckoutButton = this.querySelector(
      "[data-disabled-checkout]"
    );
    this.checkoutWithSubscriptionButton = this.querySelector(
      "[data-checkout-with-subscription]"
    );
    this.checkoutButton = this.querySelector("[data-checkout]");
    // Initialize empty products object to prevent errors during transition
    this.products = {};
    this.itemPopupTriggers = this.querySelectorAll(
      "[data-bundle-item-popup-trigger]"
    );
    this.itemPopupCloseTriggers = this.querySelectorAll("[data-popup-close]");

    // Make sure no checkout buttons are disabled on load
    this.checkoutButton.disabled = false;
    this.checkoutButton.classList.remove("loading");
    this.checkoutWithSubscriptionButton.disabled = false;
    this.checkoutWithSubscriptionButton.classList.remove("loading");

    this.clearBundleButton = this.querySelectorAll("[data-clear-bundle]");

    // Nutrition facts pop-up handling
    this.itemPopupTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        const closesGridItem = trigger.closest("[data-bundle-grid-footer]");
        const popup = closesGridItem?.querySelector("[data-bundle-item-popup]");

        // Lock scroll on body
        document.body.style.overflow = "hidden";

        if (popup) {
          popup.classList.add("active");
        }
      });
    });

    this.itemPopupCloseTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        const popUp = trigger.closest("[data-bundle-item-popup]");
        popUp.classList.remove("active");
        document.body.style.overflow = "";
      });
    });

    // Document-level listeners should only be added once (shared across all instances)
    if (!BundleBuilder.documentListenersInitialized) {
      BundleBuilder.documentListenersInitialized = true;

      document.addEventListener("click", (event) => {
        if (event.target.closest("[data-bundle-item-popup-trigger]")) return;

        // If you clicked inside the popup container, ignore
        if (event.target.closest(".grid-popup-container")) return;

        // Otherwise, close all active popups in all bundle-builder instances
        document
          .querySelectorAll("bundle-builder [data-bundle-item-popup].active")
          .forEach((pop) => {
            pop.classList.remove("active");
            document.body.style.overflow = "";
          });
      });
    }

    // Initialize quantity selectors - use event delegation scoped to this element
    this.addEventListener("click", (event) => {
      if (event.target.matches("[data-bundle-qty-control]")) {
        const qtySelector = event.target.closest("[data-qty-selector]");
        if (qtySelector) {
          // Don't await - let debounced handler manage the timing
          this.handleQtyChange(
            qtySelector,
            event.target.dataset.bundleQtyControl
          );
        }
      }
    });

    // Handle Enter key on quantity inputs
    this.addEventListener("keydown", (event) => {
      if (event.target.matches("input.bundle-qty-input") && event.key === "Enter") {
        event.preventDefault();
        const qtySelector = event.target.closest("[data-qty-selector]");
        if (qtySelector) {
          this.handleDirectQuantityInput(qtySelector);
        }
      }
    });

    // Initialize remove item buttons - scoped to this element
    this.addEventListener("click", async (event) => {
      const removeButton = event.target.closest("[data-remove-item]");
      if (removeButton) {
        event.preventDefault();
        await this.handleRemoveItem(removeButton);
      }
    });

    // Toggle bundle summary visibility
    if (this.bundleSummaryToggle) {
      this.bundleSummaryToggle.addEventListener("click", () => {
        if (this.bundleSummary) {
          this.bundleSummary.toggleAttribute("data-active");

          // If the summary is active, we can lock scroll on body
          if (this.bundleSummary.hasAttribute("data-active")) {
            document.body.style.overflow = "hidden";
            // Add query parameter to persist open state
            this.addSummaryActiveParam();
          } else {
            document.body.style.overflow = "";
            // Remove query parameter when closed
            this.removeSummaryActiveParam();
          }
        }
      });
    }

    if (this.closeSummary) {
      // loop through all close summary buttons
      this.closeSummary.forEach((button) => {
        button.addEventListener("click", () => {
          if (this.bundleSummary) {
            this.bundleSummary.removeAttribute("data-active");
            document.body.style.overflow = "";
            // Remove query parameter when closed
            this.removeSummaryActiveParam();
          }
        });
      });
    }

    // Toggle selling plan
    this.sellingPlanInputs.forEach((input) => {
      input.addEventListener("change", async (event) => {
        this.sellingPlan = event.target.value;

        // Save selection to localStorage
        localStorage.setItem("bundle-selling-plan", this.sellingPlan);

        // Remove data-selected from all selling plan input parents
        this.sellingPlanInputs.forEach((el) => {
          const parent = el.closest(".bundle-summary__option");
          parent?.removeAttribute("data-selected");
        });

        // Add data-selected to the parent of the selected input
        const selectedParent = event.target.closest(".bundle-summary__option");
        selectedParent?.setAttribute("data-selected", "");

        // Show/hide bundle discount badges based on selling plan
        this.toggleBundleDiscountBadges();

        // Update cart items with the selected selling plan
        await this.updateCartForSellingPlanChange();

        this.evaluateBundleAndUpdate();
      });
    });

    // Pack option handling when selected
    this.packOption.forEach((option) => {
      option.addEventListener("change", () => {
        const discountValue = option.dataset.discount;
        const sellingPlanOptions = this.querySelectorAll(
          "[data-selling-plan-options]"
        );
        if (sellingPlanOptions.length > 0) {
          sellingPlanOptions.forEach((el) => {
            const discountAmounts = el.querySelectorAll(
              "[data-discount-amount]"
            );
            discountAmounts.forEach((amount) => {
              amount.textContent = discountValue;
            });
          });
        }
      });
    });

    // Handle checkout button click
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener("click", async () => {
        this.addAllToCart(false);
      });
    }

    // Handle checkout with subscription button click
    if (this.checkoutWithSubscriptionButton) {
      this.checkoutWithSubscriptionButton.addEventListener(
        "click",
        async () => {
          // add selling plan to the product
          this.addAllToCart(true);
        }
      );
    }

    // Clear bundle
    if (this.clearBundleButton) {
      this.clearBundleButton.forEach((button) => {
        button.addEventListener("click", async (event) => {
          event.preventDefault();

          // Clear all items from cart
          await this.clearAllCartItems();

          // Hide all action controls
          this.querySelectorAll(".product-grid-item__bundle-actions").forEach(
            (el) => {
              el.classList.remove("controls-active");
              const qtyInput = el.querySelector("input.bundle-qty-input");
              if (qtyInput) {
                qtyInput.value = "";
              }
            }
          );

          // Remove all discount badges
          this.querySelectorAll("[data-discount-badge]").forEach((badge) => {
            badge.remove();
          });
        });
      });
    }

    // Apply discount code
    const applyDiscountButton = this.querySelector("[data-apply-discount]");
    if (applyDiscountButton) {
      applyDiscountButton.addEventListener("click", async (event) => {
        event.preventDefault();
        await this.handleApplyDiscount();
      });
    }

    // Apply discount on Enter key
    const discountInput = this.querySelector("[data-discount-code]");
    if (discountInput) {
      discountInput.addEventListener("keydown", async (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          await this.handleApplyDiscount();
        }
      });
    }

    // Remove discount code - scoped to this element
    this.addEventListener("click", async (event) => {
      const removeButton = event.target.closest("[data-remove-discount]");
      if (removeButton) {
        event.preventDefault();
        const discountCode = removeButton.getAttribute("data-remove-discount");
        await this.handleRemoveDiscount(discountCode);
      }
    });

    // Toggle cart note textarea
    const cartNoteLabel = document.querySelector(".cart__notes__label");
    const cartNoteTextarea = document.getElementById("CartSpecialInstructions");
    if (cartNoteLabel && cartNoteTextarea) {
      cartNoteLabel.addEventListener("click", () => {
        // Toggle the data-visible attribute
        if (cartNoteTextarea.hasAttribute("data-visible")) {
          cartNoteTextarea.removeAttribute("data-visible");
          // Clear the textarea value when hiding
          cartNoteTextarea.value = "";
          // Update aria-expanded
          cartNoteLabel.setAttribute("aria-expanded", "false");
        } else {
          cartNoteTextarea.setAttribute("data-visible", "");
          // Update aria-expanded
          cartNoteLabel.setAttribute("aria-expanded", "true");
        }
      });
    }

    // Initialize cart sync - no more localStorage
    this.initCartSync();

    // Make sure checkout buttons are enabled on load
    this.checkoutButton.disabled = false;
    this.checkoutButton.classList.remove("loading");
    this.checkoutWithSubscriptionButton.disabled = false;
    this.checkoutWithSubscriptionButton.classList.remove("loading");
  }

  async initCartSync() {
    // Load Shopify cart and update UI - no localStorage needed
    await this.loadCartItems();

    // Clean up any orphaned curated bundle child items
    await this.cleanupOrphanedCuratedBundleItems();

    // Show optimistic pricing immediately for better perceived performance
    this.evaluateBundleAndUpdate(false);

    // Refresh HTML for items to ensure proper metadata is loaded
    // Skip this during selling plan changes to avoid fetching stale HTML
    const hasAnyItems =
      this.itemsContainer.querySelectorAll("[data-bundle-item]").length > 0;
    if (hasAnyItems && !this.isSellingPlanChanging) {
      await this.refreshBundleItemsHTML();
      // After refreshing HTML, update line keys to match current cart
      this.updateDOMLineKeys();
    }

    // DON'T rebuild cart on page load - the cart is already in the correct state
    // Only rebuild when user actively changes selling plan selection

    // Update UI with final accurate data
    this.evaluateBundleAndUpdate(false);

    // Show/hide bundle discount badges based on saved selling plan
    this.toggleBundleDiscountBadges();

    // Check if bundle summary should be open based on URL parameter
    this.checkSummaryActiveParam();

    // Enable action buttons after initialization
    this.enableBundleSummaryActions();

    // Remove loading state from clear bundle buttons AFTER all initialization is complete
    // This prevents race conditions if user clicks "Clear All" during cart sync
    this.clearBundleButton.forEach((button) => {
      button.classList.remove("loading");
    });
  }

  addSummaryActiveParam() {
    // Add ?summary=open to the URL
    const url = new URL(window.location);
    url.searchParams.set("summary", "open");
    window.history.pushState({}, "", url);
  }

  removeSummaryActiveParam() {
    // Remove ?summary=open from the URL
    const url = new URL(window.location);
    url.searchParams.delete("summary");
    window.history.pushState({}, "", url);
  }

  checkSummaryActiveParam() {
    // Check if URL has ?summary=open parameter
    const url = new URL(window.location);
    if (url.searchParams.get("summary") === "open") {
      if (this.bundleSummary) {
        this.bundleSummary.setAttribute("data-active", "");
        document.body.style.overflow = "hidden";
      }
    }
  }

  processCartData(cart) {
    // Helper method to process cart data from any source
    this.cartItems = cart.items || [];
    this.cartTotal = cart.total_price || 0; // Store cart total (in cents) - after discounts
    this.cartOriginalTotal = cart.original_total_price || cart.total_price || 0; // Before discounts
    this.cartTotalDiscount = cart.total_discount || 0; // Total discount amount in cents

    // Analyze discount applications
    const discountApplications = cart.cart_level_discount_applications || [];

    // Normalize discount applications - handle both formats:
    // Format 1 (from /cart.js): [{ type, title, ... }]
    // Format 2 (from /cart/change.js): [{ discount_application: { type, title, ... } }]
    const normalizedDiscounts = discountApplications.map((item) => {
      // If it has a nested discount_application property, use that
      return item.discount_application || item;
    });

    // Check if there's a discount_code type discount
    this.hasDiscountCode = normalizedDiscounts.some(
      (discount) => discount.type === "discount_code"
    );

    // Check if there are OTHER discount types (automatic, script, etc.)
    this.hasOtherDiscounts = normalizedDiscounts.some(
      (discount) => discount.type !== "discount_code"
    );

    // Get the discount code amount specifically (in cents)
    const discountCodeApplication = normalizedDiscounts.find(
      (discount) => discount.type === "discount_code"
    );
    this.discountCodeAmount = discountCodeApplication
      ? discountCodeApplication.total_allocated_amount
      : 0;

    // Check if welcome15off discount is active (case-insensitive)
    this.hasWelcome15Off = normalizedDiscounts.some(
      (discount) =>
        discount.type === "discount_code" &&
        discount.title &&
        discount.title.toLowerCase() === "welcome15off"
    );
  }

  async _loadCartItemsInternal() {
    // Internal method - does NOT use queue
    // Use this when already inside a queued operation
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      this.processCartData(cart);
    } catch (err) {
      console.error("Failed to load cart items:", err);
      this.cartItems = [];
      this.cartTotal = 0;
      this.cartOriginalTotal = 0;
      this.cartTotalDiscount = 0;
      this.hasDiscountCode = false;
      this.hasOtherDiscounts = false;
      this.discountCodeAmount = 0;
    }
  }

  async loadCartItems() {
    // Queue this to ensure it happens in sequence with cart modifications
    // This prevents reading stale cart state after rapid add/update operations
    return await this.enqueueRequest(async () => {
      await this._loadCartItemsInternal();
    });
  }

  disableQuantityControls(variantId) {
    // Disable grid item controls
    const gridActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );
    gridActions.forEach((actionElem) => {
      const buttons = actionElem.querySelectorAll("button");
      const input = actionElem.querySelector("input");
      buttons.forEach((btn) => {
        btn.disabled = true;
      });
      if (input) {
        input.disabled = true;
      }
      actionElem.style.opacity = "0.5";
      actionElem.style.pointerEvents = "none";
    });

    // Disable bundle item controls
    const bundleItems = this.querySelectorAll(
      `[data-bundle-item][data-variant="${variantId}"]`
    );
    bundleItems.forEach((item) => {
      const qtySelector = item.querySelector("[data-qty-selector]");
      if (qtySelector) {
        const buttons = qtySelector.querySelectorAll("button");
        const input = qtySelector.querySelector("input");
        buttons.forEach((btn) => {
          btn.disabled = true;
        });
        if (input) {
          input.disabled = true;
        }
        qtySelector.style.opacity = "0.5";
        qtySelector.style.pointerEvents = "none";
      }
    });
  }

  enableQuantityControls(variantId) {
    // Enable grid item controls
    const gridActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );
    gridActions.forEach((actionElem) => {
      const buttons = actionElem.querySelectorAll("button");
      const input = actionElem.querySelector("input");
      buttons.forEach((btn) => {
        btn.disabled = false;
      });
      if (input) {
        input.disabled = false;
      }
      actionElem.style.opacity = "";
      actionElem.style.pointerEvents = "";
    });

    // Enable bundle item controls
    const bundleItems = this.querySelectorAll(
      `[data-bundle-item][data-variant="${variantId}"]`
    );
    bundleItems.forEach((item) => {
      const qtySelector = item.querySelector("[data-qty-selector]");
      if (qtySelector) {
        const buttons = qtySelector.querySelectorAll("button");
        const input = qtySelector.querySelector("input");
        buttons.forEach((btn) => {
          btn.disabled = false;
        });
        if (input) {
          input.disabled = false;
        }
        qtySelector.style.opacity = "";
        qtySelector.style.pointerEvents = "";
      }
    });
  }

  async addSingleItemToCart(variantId, quantity, properties, sellingPlan) {
    const cleanedProperties = this.cleanBundleProperties(properties);

    const item = {
      quantity: quantity,
      id: variantId,
      properties: cleanedProperties,
    };

    // Only add selling plan if it exists and we're in subscription mode
    if (sellingPlan && this.sellingPlan === "subscription") {
      item.selling_plan = sellingPlan.id;
    }

    // Disable quantity controls while adding
    this.disableQuantityControls(variantId);

    // Enqueue this request to prevent race conditions
    try {
      const result = await this.enqueueRequest(async () => {
        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(item),
          });

          if (!response.ok) {
            throw new Error("Failed to add item to cart");
          }

          return await response.json();
        } catch (error) {
          console.error("Error adding single item to cart:", error);
          throw error;
        }
      });

      // Reload cart immediately after adding to ensure this.cartItems is fresh
      // This MUST happen before re-enabling controls so subsequent clicks use fresh data
      await this.loadCartItems();
      this.updateDOMLineKeys();

      return result;
    } finally {
      // Re-enable quantity controls after cart is reloaded
      this.enableQuantityControls(variantId);
    }
  }

  async updateCartItemQuantity(itemIdentifier, newQuantity) {
    // Enqueue this request to prevent race conditions
    return await this.enqueueRequest(async () => {
      try {
        // itemIdentifier can be either a lineKey or a variantId
        let cartItem;

        // First try to find by line key (unique identifier)
        cartItem = this.cartItems.find((item) => item.key === itemIdentifier);

        // If not found by key, try by variant ID (for backwards compatibility)
        if (!cartItem) {
          cartItem = this.cartItems.find(
            (item) => item.variant_id.toString() === itemIdentifier.toString()
          );
        }

        if (!cartItem) {
          console.warn(`Cart item not found for identifier: ${itemIdentifier}`);
          return null;
        }

        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: cartItem.key,
            quantity: newQuantity,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to update cart item quantity");
        }

        // Return the updated cart data - includes full cart with totals, discounts, etc.
        const updatedCart = await response.json();
        return updatedCart;
      } catch (error) {
        console.error("Error updating cart item quantity:", error);
        throw error;
      }
    });
  }

  async removeCartItem(itemIdentifier, variantId = null) {
    // Enqueue this request to prevent race conditions
    return await this.enqueueRequest(async () => {
      try {
        // itemIdentifier can be either a lineKey or a variantId
        let cartItem;

        // First try to find by line key (unique identifier)
        cartItem = this.cartItems.find((item) => item.key === itemIdentifier);

        // If not found by key, try by variant ID (for backwards compatibility or stale keys)
        // This handles cases where the lineKey from DOM is outdated
        if (!cartItem && variantId) {
          // Look up the FIRST matching variant that still exists in cart
          // This ensures we remove one item at a time even with rapid operations
          cartItem = this.cartItems.find(
            (item) => item.variant_id.toString() === variantId.toString()
          );
        }

        // Final fallback: try finding by the identifier as variantId
        if (!cartItem) {
          cartItem = this.cartItems.find(
            (item) => item.variant_id.toString() === itemIdentifier.toString()
          );
        }

        if (!cartItem) {
          console.warn(`Cart item not found for removal: ${itemIdentifier}, already removed`);
          // Item already removed by a previous operation, just refresh and return
          await this._loadCartItemsInternal();
          return;
        }

        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: cartItem.key,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to remove cart item");
        }

        const result = await response.json();

        // Refresh cart items to keep in sync (use internal version to avoid nested queue)
        await this._loadCartItemsInternal();
        console.log(`[removeCartItem] Successfully removed. Cart now has ${this.cartItems.length} items`);
        return result;
      } catch (error) {
        console.error("Error removing cart item:", error);
        throw error;
      }
    });
  }

  async updateCart(payload) {
    // Enqueue this request to prevent race conditions
    // Payload can contain: updates (object), note (string), discount (string), etc.
    return await this.enqueueRequest(async () => {
      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error("Failed to update cart");
        }

        return await response.json();
      } catch (error) {
        console.error("Error updating cart:", error);
        throw error;
      }
    });
  }

  getExistingBundleId() {
    // Check if there are existing cart items with _rc_bundle property in subscription mode
    // If so, extract and return that bundle ID so new items use the same one
    if (this.sellingPlan === "subscription" && this.cartItems && this.cartItems.length > 0) {
      for (const item of this.cartItems) {
        if (item.properties && item.properties._rc_bundle) {
          const rcBundleValue = String(item.properties._rc_bundle);
          if (rcBundleValue.includes(":")) {
            // Extract the bundle ID part (before the colon)
            return rcBundleValue.split(":")[0];
          }
        }
      }
    }
    // No existing bundle ID found, use the generated one
    return this.bundleId;
  }

  cleanBundleProperties(properties = {}) {
    const cleaned = { ...properties };

    // Always assign bundle builder id for tracking
    cleaned._bundle_builder_id = this.bundleId;

    if (this.sellingPlan === "one-time") {
      // Remove all _rc_bundle properties
      Object.keys(cleaned).forEach((key) => {
        if (key.startsWith("_rc_bundle")) {
          delete cleaned[key];
        }
      });
    } else if (this.sellingPlan === "subscription") {
      // Copy all ReCharge properties from an existing cart item
      // This ensures we have all the correct _rc_ properties without additional API calls
      // All items in the bundle share the same _rc_bundle value (using the bundle parent's product ID)
      if (this.cartItems && this.cartItems.length > 0) {
        const existingItemWithRcProps = this.cartItems.find(
          item => item.properties && item.properties._rc_bundle
        );

        if (existingItemWithRcProps) {
          // Copy all _rc_ properties from existing cart item exactly as-is
          Object.keys(existingItemWithRcProps.properties).forEach((key) => {
            if (key.startsWith("_rc_")) {
              cleaned[key] = existingItemWithRcProps.properties[key];
            }
          });
        }
      }
    }

    return cleaned;
  }

  async syncProductsWithCart() {
    const updatedProducts = {};

    // Bundle-builder always shows ALL cart items (it IS the cart now)
    this.cartItems.forEach((cartItem) => {
      const variantId = cartItem.variant_id.toString();
      const hasBundleProp = cartItem.properties?._rc_bundle;
      const hasBuilderId = cartItem.properties?._bundle_builder_id;

      const existingProduct = this.products[variantId];

      updatedProducts[variantId] = {
        line: cartItem.key,
        variantId: cartItem.variant_id,
        title: cartItem.title,
        image: cartItem.image,
        currentQuantity: cartItem.quantity,
        price: cartItem.price / 100,
      };

      if (existingProduct) {
        // Update existing product properties from local storage
        updatedProducts[variantId].properties = {
          ...existingProduct.properties,
        };

        const sellingPlan = existingProduct.sellingPlan
          ? existingProduct.sellingPlan
          : null;

        if (sellingPlan) {
          updatedProducts[variantId].sellingPlan = sellingPlan;
        }
      } else {
        // Item exists in cart but not in local storage - extract properties from cart
        updatedProducts[variantId].properties = cartItem.properties || {};

        // Bundle-builder shows all items but preserves their original properties
        // Properties are only modified during checkout, not during display
      }
    });

    // Check for items in local storage that aren't in cart (need to be added)
    const localOnlyItems = Object.keys(this.products).filter((variantId) => {
      const cartItem = this.cartItems.find(
        (item) =>
          item.variant_id.toString() === variantId &&
          (item.properties?._bundle_builder_id || item.properties?._rc_bundle)
      );
      return !cartItem;
    });

    // Add local-only items to cart in background (don't wait)
    if (localOnlyItems.length > 0) {
      Promise.all(
        localOnlyItems.map(async (variantId) => {
          const product = this.products[variantId];
          try {
            await this.addSingleItemToCart(
              variantId,
              product.currentQuantity,
              product.properties,
              product.sellingPlan
            );
          } catch (error) {
            console.error(
              "Failed to sync local item to cart:",
              variantId,
              error
            );
          }
        })
      ).catch((error) => {
        console.error("Some items failed to sync to cart:", error);
      });

      // Include local items in the updated products regardless of sync success
      localOnlyItems.forEach((variantId) => {
        updatedProducts[variantId] = this.products[variantId];
      });
    }

    // Since we're working directly with cart now, just update the UI
    this.evaluateBundleAndUpdate();
  }

  evaluateBundleAndUpdate(useOptimisticPricing = false) {
    // Update bundle state and discount tier FIRST so it's used in pricing calculations
    if (this.selectedPack) {
      this.toggleCheckoutActive();
    }

    this.updateProgressBar(useOptimisticPricing);
    // Update discount amount BEFORE calculating totals so the discount is included
    if (this.selectedPack) {
      this.updateDiscountAmount(this.selectedPack.dataset.discount);
    }
    this.updateTotal(useOptimisticPricing);
    this.toggleSellingPlan();

    // Update individual item prices based on selling plan and discount
    this.updateItemPrices();

    // Update checkout button state based on pending updates
    this.updateCheckoutButtonState();

    // Show or hide the clear bundle button based on DOM items (for immediate feedback)
    const domItems =
      this.itemsContainer?.querySelectorAll("[data-bundle-item]");
    const hasItems = domItems && domItems.length > 0;

    if (hasItems) {
      this.clearBundleButton?.forEach((button) => {
        button.removeAttribute("hidden");
      });
    } else {
      this.clearBundleButton?.forEach((button) => {
        button.setAttribute("hidden", "");
      });
    }

    // Update the grid item actions inputs and visibility based on what's actually in the cart
    // For optimistic updates, use DOM. For server updates, use cart items.
    const variantQuantities = new Map();

    if (useOptimisticPricing) {
      // OPTIMISTIC: Use DOM for instant feedback (items may have been added/removed optimistically)
      const domItems =
        this.itemsContainer?.querySelectorAll("[data-bundle-item]") || [];
      domItems.forEach((item) => {
        // Skip visual curated parent items
        if (item.hasAttribute("data-visual-curated-parent")) return;

        const variantId = item.getAttribute("data-variant");
        const qty = parseInt(item.getAttribute("data-qty"), 10) || 0;

        if (variantId) {
          const currentQty = variantQuantities.get(variantId) || 0;
          variantQuantities.set(variantId, currentQty + qty);
        }
      });
    } else {
      // SERVER: Use cart items for accurate state after server sync
      this.cartItems.forEach((cartItem) => {
        const variantId = cartItem.variant_id.toString();
        const currentQty = variantQuantities.get(variantId) || 0;
        variantQuantities.set(variantId, currentQty + (cartItem.quantity || 0));
      });
    }

    // First, hide ALL grid controls
    const allGridControls = document.querySelectorAll(
      "[data-grid-item-actions]"
    );
    allGridControls.forEach((actionElem) => {
      actionElem.classList.remove("controls-active");
      const qtyInput = actionElem.querySelector("input.bundle-qty-input");
      if (qtyInput) {
        qtyInput.value = "";
      }
    });

    // Then show/update only the ones that have items in the cart
    variantQuantities.forEach((totalQuantity, variantId) => {
      const gridItemActions = document.querySelectorAll(
        `[data-grid-item-actions="${variantId}"]`
      );
      if (gridItemActions) {
        gridItemActions.forEach((actionElem) => {
          actionElem.classList.add("controls-active");

          // Update the quantity input to show the total quantity for this variant
          const qtyInput = actionElem.querySelector("input.bundle-qty-input");
          if (qtyInput) {
            qtyInput.value = totalQuantity;
          }
        });
      }
    });
  }

  updateCheckoutButtonState() {
    // Disable checkout buttons if there are pending quantity updates or cart operations
    const hasPendingUpdates = this.pendingQuantityUpdates.size > 0;
    const isCartUpdating = this.isCartUpdating || false;

    if (hasPendingUpdates || isCartUpdating) {
      this.checkoutButton.disabled = true;
      this.checkoutButton.classList.add("loading");
      this.checkoutWithSubscriptionButton.disabled = true;
      this.checkoutWithSubscriptionButton.classList.add("loading");
    } else {
      this.checkoutButton.disabled = false;
      this.checkoutButton.classList.remove("loading");
      this.checkoutWithSubscriptionButton.disabled = false;
      this.checkoutWithSubscriptionButton.classList.remove("loading");
    }
  }

  disableBundleSummaryActions() {
    if (!this.bundleSummary) return;

    // Disable checkout buttons
    const checkoutButtons = this.bundleSummary.querySelectorAll(
      "[data-checkout], [data-checkout-with-subscription]"
    );
    checkoutButtons.forEach((button) => {
      button.disabled = true;
    });

    // Disable selling plan radio inputs
    const sellingPlanInputs = this.bundleSummary.querySelectorAll(
      "[data-selling-plan]"
    );
    sellingPlanInputs.forEach((input) => {
      input.disabled = true;
    });

    // Disable pack option inputs
    const packOptionInputs =
      this.bundleSummary.querySelectorAll("[data-pack-option]");
    packOptionInputs.forEach((input) => {
      input.disabled = true;
    });

    // Disable quantity control buttons
    const qtyButtons = this.bundleSummary.querySelectorAll(
      "[data-bundle-qty-control]"
    );
    qtyButtons.forEach((button) => {
      button.disabled = true;
    });
  }

  enableBundleSummaryActions() {
    if (!this.bundleSummary) return;

    // Enable checkout buttons
    const checkoutButtons = this.bundleSummary.querySelectorAll(
      "[data-checkout], [data-checkout-with-subscription]"
    );
    checkoutButtons.forEach((button) => {
      button.disabled = false;
    });

    // Enable selling plan radio inputs
    const sellingPlanInputs = this.bundleSummary.querySelectorAll(
      "[data-selling-plan]"
    );
    sellingPlanInputs.forEach((input) => {
      input.disabled = false;
    });

    // Enable pack option inputs
    const packOptionInputs =
      this.bundleSummary.querySelectorAll("[data-pack-option]");
    packOptionInputs.forEach((input) => {
      input.disabled = false;
    });

    // Enable quantity control buttons
    const qtyButtons = this.bundleSummary.querySelectorAll(
      "[data-bundle-qty-control]"
    );
    qtyButtons.forEach((button) => {
      button.disabled = false;
    });
  }

  async updateCartForSellingPlanChange() {
    // This method is called when the user switches between one-time and subscription
    // We clear the cart and re-add items with the correct selling plans

    try {
      // Check if cart items already match the selected selling plan
      const needsUpdate = this.cartNeedsSellingPlanUpdate();

      if (!needsUpdate) {
        // Cart is already in the correct state, just update UI
        this.evaluateBundleAndUpdate();
        return;
      }

      // Set flag to indicate cart is updating
      this.isCartUpdating = true;
      this.isSellingPlanChanging = true; // Flag to skip HTML refresh
      this.updateCheckoutButtonState();

      // Clear cart and re-add items with correct selling plans
      await this.rebuildCartWithSellingPlans();

      // Reload cart to get updated pricing and discount data from Shopify
      await this.loadCartItems();

      // Update DOM line keys to match the new cart state after rebuild
      this.updateDOMLineKeys();

      // Clear flags
      this.isCartUpdating = false;
      this.isSellingPlanChanging = false;

      this.evaluateBundleAndUpdate();
    } catch (error) {
      console.error("Error updating cart for selling plan change:", error);
      this.isCartUpdating = false;
      this.isSellingPlanChanging = false;
      this.updateCheckoutButtonState();
    }
  }

  cartNeedsSellingPlanUpdate() {
    // Check if any cart items need to be updated based on selling plan
    if (this.cartItems.length === 0) return false;

    const wantsSubscription = this.sellingPlan === "subscription";

    // Check if cart items have selling plans when they should (or don't when they shouldn't)
    for (const item of this.cartItems) {
      const hasSellingPlan =
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan;

      if (wantsSubscription && !hasSellingPlan) {
        // Want subscription but item doesn't have selling plan
        return true;
      }
      if (!wantsSubscription && hasSellingPlan) {
        // Want one-time but item has selling plan
        return true;
      }
    }

    return false;
  }

  async rebuildCartWithSellingPlans() {
    // Clear cart and re-add all items with the correct selling plans in a single batch
    const cartNote =
      document.getElementById("CartSpecialInstructions")?.value || "";

    // Save current cart items before clearing
    const originalCartItems = [...this.cartItems];

    // On cart page, treat ALL items as bundle items (not just those with bundle properties)
    // This allows items added from other collection pages to work properly
    const bundleCartItems = originalCartItems.filter(
      (item) =>
        // Exclude curated bundle children (they're children of bundle products)
        !item.properties?.curated_bundle_item
    );

    const isSubscription = this.sellingPlan === "subscription";

    // Build items array for batch add
    const items = bundleCartItems.map((cartItem) => {
      const variantId = cartItem.variant_id;
      const lineKey = cartItem.key;

      // Check if this item is subscription-eligible by looking at the DOM using line key
      const domItem = this.querySelector(
        `[data-bundle-item][data-line="${lineKey}"]`
      );
      const isSubscriptionEligible = domItem?.hasAttribute(
        "data-subscription-eligible"
      );

      // Build properties
      const properties = {
        ...cartItem.properties,
        _bundle_builder_id: this.bundleId,
      };

      // Only add subscription properties to eligible items
      if (isSubscription && isSubscriptionEligible) {
        // Add ReCharge properties from window.bundleData
        if (window.bundleData && window.bundleData.properties) {
          Object.keys(window.bundleData.properties).forEach((key) => {
            if (key.startsWith("_rc_")) {
              properties[key] = window.bundleData.properties[key];
            }
          });
        }

        // Ensure _rc_bundle has the correct format: bundleId:originalBundleProductId
        const rcBundleValue = String(properties._rc_bundle || "");
        const originalBundleProductId = rcBundleValue.includes(":")
          ? rcBundleValue.split(":")[1]
          : rcBundleValue;
        properties._rc_bundle = originalBundleProductId
          ? `${this.bundleId}:${originalBundleProductId}`
          : this.bundleId;
      } else {
        // Remove all _rc_ properties for one-time purchase or non-eligible items
        Object.keys(properties).forEach((key) => {
          if (key.startsWith("_rc_")) {
            delete properties[key];
          }
        });
      }

      const item = {
        id: variantId,
        quantity: cartItem.quantity,
        properties: properties,
      };

      // Add selling plan only if subscription mode AND item is eligible
      if (isSubscription && isSubscriptionEligible) {
        const sellingPlan = this.getSellingPlanFromDOM(variantId);
        if (sellingPlan && sellingPlan.id) {
          item.selling_plan = sellingPlan.id;
        }
      }

      return item;
    });

    // Clear the cart by setting all line items to quantity 0 using their unique keys
    const updates = {};
    originalCartItems.forEach((item) => {
      // Use line item key (unique) instead of variant_id to handle duplicates
      updates[item.key] = 0;
    });

    await this.updateCart({ updates });

    // Add all items back in a single batch request
    const addResponse = await this.enqueueRequest(async () => {
      return await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      });
    });

    if (!addResponse.ok) {
      const responseText = await addResponse.text();
      throw new Error(`Failed to add items back to cart: ${responseText}`);
    }

    // Restore cart note if provided
    if (cartNote) {
      await this.updateCart({ note: cartNote });
    }
  }

  async addAllToCart(useSellingPlan = false) {
    this.checkoutButton.disabled = true;
    this.checkoutButton.classList.add("loading");
    this.checkoutWithSubscriptionButton.disabled = true;
    this.checkoutWithSubscriptionButton.classList.add("loading");

    try {
      // Cart is already in the correct state from updateCartForSellingPlanChange()
      // Just need to update the cart note if there is one
      const cartNoteTextarea = document.getElementById(
        "CartSpecialInstructions"
      );
      const cartNote = cartNoteTextarea ? cartNoteTextarea.value : "";

      if (cartNote) {
        // Update cart note only
        await this.updateCart({ note: cartNote });
      }

      // Save bundle id for session tracking
      localStorage.setItem("bundle-builder-id", this.bundleId);

      // Redirect based on current page
      // If we're on the bundle builder page, go to cart; if on cart page, go to checkout
      const isCartPage = document.body.classList.contains("template-cart");
      window.location.href = isCartPage ? "/checkout" : "/cart";
    } catch (error) {
      console.error("Error updating cart:", error);
      this.checkoutButton.disabled = false;
      this.checkoutButton.classList.remove("loading");
      this.checkoutWithSubscriptionButton.disabled = false;
      this.checkoutWithSubscriptionButton.classList.remove("loading");
    }
  }

  async replaceCartItemsWithSubscription(cartNote = "") {
    // Clear all bundle items from cart and re-add with selling plans
    // This approach is more reliable than concurrent updates for subscriptions

    // 1. Identify all items currently in cart (save original state)
    const originalCartItems = [...this.cartItems]; // Save original cart state
    // Process ALL items in cart, not just bundle items
    const bundleCartItems = originalCartItems;

    // 2. Remove all existing bundle items
    // Include the cart note in this update
    if (bundleCartItems.length > 0) {
      const updates = bundleCartItems.reduce((acc, item) => {
        acc[item.id] = 0;
        return acc;
      }, {});

      const removeResponse = await this.updateCart({ updates, note: cartNote });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove existing bundle items from cart");
      }
    }

    // 3. Prepare new items with selling plans and subscription properties
    // Consolidate items by variant_id to avoid duplicates
    const itemsByVariant = new Map();

    for (const cartItem of bundleCartItems) {
      const variantId = cartItem.variant_id.toString();

      if (itemsByVariant.has(variantId)) {
        // Add to existing quantity
        itemsByVariant.get(variantId).quantity += cartItem.quantity;
      } else {
        // Get selling plan and product data
        const sellingPlan = this.getSellingPlanFromDOM(variantId);
        const originalProductData = this.getProductDataFromDOM(variantId);

        // Prepare properties with subscription metadata
        const properties = {
          ...cartItem.properties,
          _bundle_builder_id: this.bundleId,
        };

        // Add all ReCharge properties from original product data
        if (originalProductData && originalProductData.properties) {
          Object.keys(originalProductData.properties).forEach((key) => {
            if (key.startsWith("_rc_")) {
              properties[key] = originalProductData.properties[key];
            }
          });
        }

        // Set _rc_bundle with format: bundleId:originalBundleProductId
        // Keep the original bundle product ID that was set in the template
        const originalBundleProductId = properties._rc_bundle || "";
        properties._rc_bundle = `${this.bundleId}:${originalBundleProductId}`;

        const newItem = {
          quantity: cartItem.quantity,
          id: variantId,
          properties: properties,
        };

        // Add selling plan if available
        if (sellingPlan && sellingPlan.id) {
          newItem.selling_plan = sellingPlan.id;
        } else {
          console.warn(`No selling plan found for variant ${variantId}`);
        }

        itemsByVariant.set(variantId, newItem);
      }
    }

    // Convert map to array
    const newItems = Array.from(itemsByVariant.values());

    // 4. Add all items back to cart with selling plans
    if (newItems.length > 0) {
      const addResponse = await this.enqueueRequest(async () => {
        return await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: newItems }),
        });
      });

      if (!addResponse.ok) {
        throw new Error("Failed to add subscription items to cart");
      }
    }
  }

  // Legacy method - kept for reference but not used
  // The concurrent update approach had issues with Shopify's cart API
  async updateCartItemsForSubscriptionLegacy() {
    // Update each bundle item to include selling plan and subscription properties
    const updatePromises = this.cartItems
      .filter(
        (item) =>
          item.properties &&
          (item.properties._rc_bundle || item.properties._bundle_builder_id)
      )
      .map(async (cartItem) => {
        const variantId = cartItem.variant_id.toString();
        const product = this.products[variantId];

        if (!product) return; // Skip if product not in our bundle

        // Prepare updated properties
        const updatedProperties = {
          ...cartItem.properties,
          _bundle_builder_id: this.bundleId,
        };

        // Set _rc_bundle with format: bundleId:originalBundleProductId
        // Keep the original bundle product ID that was set in the template
        const originalBundleProductId = updatedProperties._rc_bundle || "";
        updatedProperties._rc_bundle = `${this.bundleId}:${originalBundleProductId}`;

        // Prepare change request
        const changeData = {
          id: cartItem.key,
          properties: updatedProperties,
        };

        // Get selling plan - first from stored product, then from DOM
        let sellingPlan = product.sellingPlan;

        if (!sellingPlan || !sellingPlan.id) {
          // Try to get selling plan from the product grid DOM
          sellingPlan = this.getSellingPlanFromDOM(variantId);
        }

        // Add selling plan if available
        if (sellingPlan && sellingPlan.id) {
          changeData.selling_plan = sellingPlan.id;
        } else {
          console.warn(`No selling plan found for variant ${variantId}`);
        }

        // Update cart item
        const response = await this.enqueueRequest(async () => {
          return await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(changeData),
          });
        });

        if (!response.ok) {
          throw new Error(
            `Failed to update cart item ${cartItem.key} for subscription`
          );
        }

        return response.json();
      });

    await Promise.all(updatePromises.filter(Boolean));
  }

  getSellingPlanFromDOM(variantId) {
    const productData = this.getProductDataFromDOM(variantId);
    return productData ? productData.selling_plan || null : null;
  }

  getProductDataFromDOM(variantId) {
    // Get selling plan from cart item data attributes
    const cartItem = this.itemsContainer.querySelector(
      `[data-variant="${variantId}"]`
    );
    let sellingPlan = null;

    if (cartItem) {
      const sellingPlanId = cartItem.getAttribute("data-selling-plan-id");
      const sellingPlanName = cartItem.getAttribute("data-selling-plan-name");

      if (sellingPlanId && sellingPlanName) {
        sellingPlan = {
          id: sellingPlanId,
          name: sellingPlanName,
        };
      }
    }

    // Use global bundle data for properties, cart item for selling plan
    if (window.bundleData) {
      return {
        id: variantId,
        properties: window.bundleData.properties,
        selling_plan: sellingPlan,
      };
    }

    // Fallback to DOM search if global data not available
    const gridItems = this.querySelectorAll("[data-bundle-product-json]");
    for (const gridItem of gridItems) {
      try {
        const productData = JSON.parse(gridItem.textContent || "{}");
        if (
          productData.id &&
          productData.id.toString() === variantId.toString()
        ) {
          return productData;
        }
      } catch (error) {
        console.error("Error parsing product JSON from DOM:", error);
      }
    }

    return null;
  }

  async updateCartItemsForOneTime(cartNote = "") {
    // Clear cart and re-add with correct quantities and one-time properties
    // This ensures quantities match the bundle UI state

    // 1. Identify all bundle items currently in cart (save original state)
    const originalCartItems = [...this.cartItems]; // Save original cart state
    const bundleCartItems = originalCartItems.filter(
      (item) =>
        item.properties &&
        (item.properties._rc_bundle || item.properties._bundle_builder_id)
    );

    // 2. Remove all existing bundle items and include the cart note
    if (bundleCartItems.length > 0) {
      const updates = bundleCartItems.reduce((acc, item) => {
        acc[item.id] = 0;
        return acc;
      }, {});

      const removeResponse = await this.updateCart({ updates, note: cartNote });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove existing bundle items from cart");
      }
    }

    // 3. Prepare new items with correct quantities and one-time properties
    // Consolidate items by variant_id to avoid duplicates
    const itemsByVariant = new Map();

    for (const cartItem of bundleCartItems) {
      const variantId = cartItem.variant_id.toString();

      if (itemsByVariant.has(variantId)) {
        // Add to existing quantity
        itemsByVariant.get(variantId).quantity += cartItem.quantity;
      } else {
        // Prepare properties without ReCharge subscription metadata
        const properties = {
          _bundle_builder_id: this.bundleId,
        };

        // Get original product data but exclude _rc_ properties for one-time
        const originalProductData = this.getProductDataFromDOM(variantId);
        if (originalProductData && originalProductData.properties) {
          // Only include non-ReCharge properties for one-time purchases
          Object.keys(originalProductData.properties).forEach((key) => {
            if (!key.startsWith("_rc_")) {
              properties[key] = originalProductData.properties[key];
            }
          });
        }

        itemsByVariant.set(variantId, {
          quantity: cartItem.quantity, // Keep original line item quantity
          id: variantId,
          properties: properties,
        });
      }
    }

    // Convert map to array
    const newItems = Array.from(itemsByVariant.values());

    // 4. Add all items back to cart as one-time purchases
    if (newItems.length > 0) {
      const addResponse = await this.enqueueRequest(async () => {
        return await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: newItems }),
        });
      });

      if (!addResponse.ok) {
        throw new Error("Failed to add one-time items to cart");
      }
    }
  }

  async cleanupOrphanedCuratedBundleItems() {
    // Remove any orphaned curated bundle child items from cart
    // These are items with curated_bundle_item property that shouldn't exist anymore
    const orphanedItems = this.cartItems.filter(
      (item) => item.properties && item.properties.curated_bundle_item
    );

    if (orphanedItems.length === 0) {
      return; // Nothing to clean up
    }

    // Remove orphaned items from cart
    const updates = orphanedItems.reduce((acc, item) => {
      acc[item.id] = 0;
      return acc;
    }, {});

    await this.updateCart({ updates });

    // Reload cart to get updated state
    await this.loadCartItems();
  }

  updateDOMLineKeys() {
    // Update data-line attributes in DOM to match current cart items
    // This is needed after cart rebuild operations (like subscription switches)
    if (!this.itemsContainer) return;

    const bundleItems =
      this.itemsContainer.querySelectorAll("[data-bundle-item]");

    // Track which cart items we've already matched to handle duplicates
    const usedCartItemKeys = new Set();

    bundleItems.forEach((domItem) => {
      const variantId = domItem.getAttribute("data-variant");
      if (!variantId) return;

      // Find the corresponding cart item by variant ID
      // For duplicates, find the first unmatched cart item with this variant
      const cartItem = this.cartItems.find(
        (item) =>
          item.variant_id.toString() === variantId &&
          !usedCartItemKeys.has(item.key)
      );

      if (cartItem) {
        // Update the data-line attribute with the current line key from cart
        domItem.setAttribute("data-line", cartItem.key);
        usedCartItemKeys.add(cartItem.key);
      }
    });
  }

  async refreshBundleItemsHTML() {
    // Fetch fresh cart HTML from the server
    try {
      const response = await fetch(window.location.href);
      const html = await response.text();

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Get the fresh bundle items
      const freshItemsContainer = doc.querySelector("[data-items]");

      if (freshItemsContainer && this.itemsContainer) {
        // Replace the current items with fresh ones
        this.itemsContainer.innerHTML = freshItemsContainer.innerHTML;
      }
    } catch (error) {
      console.error("Failed to refresh bundle items HTML:", error);
    }
  }

  toggleCheckoutActive() {
    const numberOfItems = this.getBundleCount();
    const numberOfSubscriptionEligibleItems = this.getBundleCount(true);

    // The pack goal is determined by the first pack option that meets or exceeds the number of ELIGIBLE items
    // (subscription-ineligible items don't count towards pack tiers)
    let packGoal = 0;
    let packGoalFound = false;
    const firstPackOption = this.packOption[0];
    for (const option of this.packOption) {
      const optionValue = parseInt(option.value, 10);
      if (optionValue >= numberOfSubscriptionEligibleItems) {
        packGoal = optionValue;
        packGoalFound = true;
        break;
      }
    }

    // Get reversed packOption array and find the first option that meets or exceeds the number of ELIGIBLE items
    const reversedPackOptions = Array.from(this.packOption).reverse();

    this.selectedPack = reversedPackOptions.find((option) => {
      return numberOfSubscriptionEligibleItems >= parseInt(option.value, 10);
    });

    if (this.selectedPack) {
      this.selectedPack.checked = true;
    } else {
      this.selectedPack = firstPackOption;
      this.selectedPack.checked = true;
    }

    const packAmountMessages = this.querySelectorAll("[data-pack-amount]");

    packAmountMessages.forEach((packAmountMessage) => {
      packAmountMessage.textContent = packGoal;
    });

    // Bundle is met if subscription-eligible items meet the minimum pack size (6)
    // This controls the data-bundle-disabled attribute which enables/disables subscription UI
    this.isBundleMet =
      numberOfSubscriptionEligibleItems >= parseInt(firstPackOption.value, 10);

    // Subscription is available only if subscription-eligible items meet minimum (6)
    const subscriptionAvailable =
      numberOfSubscriptionEligibleItems >= parseInt(firstPackOption.value, 10);

    // Store whether subscription bundle is met (for free shipping and discounts)
    this.isSubscriptionBundleMet = subscriptionAvailable;

    // Update the checkout button state
    if (numberOfItems > 0) {
      this.bundleSummary?.removeAttribute("data-empty");
      this.bundleSummaryToggleContainer?.removeAttribute("data-empty");
    } else {
      this.bundleSummary?.setAttribute("data-empty", "");
      this.bundleSummaryToggleContainer?.setAttribute("data-empty", "");

      // Reset pack option to default if no items are present
      if (this.defaultPackOption) {
        this.defaultPackOption.checked = true;
        this.defaultPackOption.dispatchEvent(new Event("change"));
        this.selectedPack = this.defaultPackOption;
        this.updateDiscountAmount(this.defaultPackOption.dataset.discount);
      }
    }

    const oneTimePurchaseOptionContainer = this.querySelector(
      ".bundle-summary__option--one-time"
    );
    const subscriptionOptionContainer = this.querySelector(
      ".bundle-summary__option--subscription"
    );

    // Disable/enable subscription option based on eligible items
    const subscriptionRadio = subscriptionOptionContainer?.querySelector(
      '[data-selling-plan][value="subscription"]'
    );
    if (subscriptionRadio) {
      subscriptionRadio.disabled = !subscriptionAvailable;
      if (!subscriptionAvailable) {
        subscriptionOptionContainer.setAttribute("data-disabled", "");
        subscriptionOptionContainer.classList.remove("has-free-shipping");
        // If subscription was selected but is no longer available, switch to one-time
        if (this.sellingPlan === "subscription") {
          const oneTimeRadio = this.querySelector(
            '[data-selling-plan][value="one-time"]'
          );
          if (oneTimeRadio) {
            oneTimeRadio.checked = true;
            oneTimeRadio.dispatchEvent(new Event("change"));
          }
        }
      } else {
        subscriptionOptionContainer.removeAttribute("data-disabled");
        // Subscriptions always get free shipping when 6+ eligible items
        subscriptionOptionContainer.classList.add("has-free-shipping");
      }
    }

    // Update the bundle summary state
    if (this.isBundleMet) {
      this.bundleSummary?.removeAttribute("data-bundle-disabled");
      this.bundleSummaryToggleContainer?.removeAttribute(
        "data-bundle-disabled"
      );

      // Show the free shipping message for one-time purchases
      if (oneTimePurchaseOptionContainer) {
        oneTimePurchaseOptionContainer.classList.add("has-free-shipping");
      }

      // Show enabled text, hide disabled text for subscription features
      const subscriptionFeatures = this.querySelectorAll(
        "[data-subscription-feature]"
      );
      subscriptionFeatures.forEach((feature) => {
        const enabledText = feature.querySelector("[data-enabled-text]");
        const disabledText = feature.querySelector("[data-disabled-text]");
        const checkmark = feature.querySelector(
          ".bundle-summary__option-check"
        );
        const featuresList = feature.closest(".bundle-summary__option-features");
        if (enabledText) enabledText.style.display = "";
        if (disabledText) disabledText.style.display = "none";
        if (checkmark) checkmark.style.display = "";
        if (featuresList) featuresList.classList.remove("is-disabled");
      });
    } else {
      this.bundleSummary?.setAttribute("data-bundle-disabled", "");
      this.bundleSummaryToggleContainer?.setAttribute(
        "data-bundle-disabled",
        ""
      );

      // Hide the free shipping message
      if (oneTimePurchaseOptionContainer) {
        oneTimePurchaseOptionContainer.classList.remove("has-free-shipping");
      }

      // Show disabled text, hide enabled text for subscription features
      const subscriptionFeatures = this.querySelectorAll(
        "[data-subscription-feature]"
      );
      subscriptionFeatures.forEach((feature) => {
        const enabledText = feature.querySelector("[data-enabled-text]");
        const disabledText = feature.querySelector("[data-disabled-text]");
        const checkmark = feature.querySelector(
          ".bundle-summary__option-check"
        );
        const featuresList = feature.closest(".bundle-summary__option-features");
        if (enabledText) enabledText.style.display = "none";
        if (disabledText) disabledText.style.display = "";
        if (checkmark) checkmark.style.display = "none";
        if (featuresList) featuresList.classList.add("is-disabled");
      });

      // Update the message to show the required amount
      const amountRequired = packGoal - numberOfItems;
      const itemsRequiredMessage = `Add ${amountRequired} more item${
        amountRequired > 1 ? "s" : ""
      } for ${packGoal}-pack`;

      // Select the one time purchase option
      const oneTimePurchaseOption = this.querySelector(
        "[data-selling-plan][value='one-time']"
      );

      if (oneTimePurchaseOption) {
        oneTimePurchaseOption.checked = true;
        this.sellingPlan = oneTimePurchaseOption.value;
        this.toggleSellingPlan();
      }
    }
  }

  updateDiscountAmount(discountValue) {
    this.querySelectorAll("[data-discount-amount]").forEach((el) => {
      el.textContent = discountValue;
    });
    this.discountAmount = parseFloat(discountValue) || 0;
    // Note: updateTotal() is called by evaluateBundleAndUpdate() after this method
  }

  toggleBundleDiscountBadges() {
    // Show/hide discount badges that contain "bundle" based on selling plan
    const discountBadges = this.querySelectorAll("[data-discount-badge]");

    discountBadges.forEach((badge) => {
      const badgeName = badge.querySelector(".discount-badge__name");
      if (badgeName) {
        const discountText = badgeName.textContent.toLowerCase();

        // Check if the discount name contains "bundle"
        if (discountText.includes("bundle")) {
          if (this.sellingPlan === "one-time") {
            // Hide bundle discounts for one-time purchases
            badge.style.display = "none";
          } else {
            // Show bundle discounts for subscription
            badge.style.display = "";
          }
        }
      }
    });
  }

  toggleSellingPlan() {
    this.bundleOptionsGrid?.setAttribute("data-badges", this.sellingPlan);
    this.bundleSummary.setAttribute("data-selling-plan", this.sellingPlan);
    this.bundleSummaryFooter?.setAttribute(
      "data-selling-plan",
      this.sellingPlan
    );

    const activeSellingPlanOption = this.querySelector(
      '[data-selling-plan][value="' + this.sellingPlan + '"]'
    );

    if (activeSellingPlanOption) {
      // Remove data-selected from all selling plan input parents
      this.sellingPlanInputs.forEach((el) => {
        const parent = el.closest(".bundle-summary__option");
        parent?.removeAttribute("data-selected");
      });

      // Add data-selected to the parent of the selected input
      const selectedParent = activeSellingPlanOption.closest(
        ".bundle-summary__option"
      );
      selectedParent?.setAttribute("data-selected", "");
    }
  }

  applySellingPlanSelection(sellingPlan) {
    // Find the radio button for the desired selling plan
    const targetOption = this.querySelector(
      `[data-selling-plan][value="${sellingPlan}"]`
    );

    if (targetOption) {
      // Uncheck all radio buttons first
      this.sellingPlanInputs.forEach((input) => {
        input.checked = false;
        const parent = input.closest(".bundle-summary__option");
        parent?.removeAttribute("data-selected");
      });

      // Check the target radio button
      targetOption.checked = true;

      // Add data-selected to its parent
      const selectedParent = targetOption.closest(".bundle-summary__option");
      selectedParent?.setAttribute("data-selected", "");

      // Update visual state
      this.bundleOptionsGrid?.setAttribute("data-badges", sellingPlan);
      this.bundleSummary?.setAttribute("data-selling-plan", sellingPlan);
      this.bundleSummaryFooter?.setAttribute("data-selling-plan", sellingPlan);
    }
  }

  generateUniqueBundleId() {
    return `bundle-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  // Queue system to prevent conflicting API requests
  async enqueueRequest(requestFn) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ requestFn, resolve, reject });
      this.processQueue();
    });
  }

  async processQueue() {
    // If already processing, don't start another process
    if (this.isProcessingQueue) return;

    // If queue is empty, we're done
    if (this.requestQueue.length === 0) return;

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const { requestFn, resolve, reject } = this.requestQueue.shift();

      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  updateProgressBar(useOptimisticPricing = false) {
    const FREE_SHIPPING_GOAL = 50.0; // $50 goal

    let cartTotal;

    if (useOptimisticPricing) {
      // Calculate optimistic total from DOM for instant feedback
      const items = this.querySelectorAll("[data-bundle-item]");
      let optimisticTotal = 0;

      items.forEach((item) => {
        // Skip visual curated parent items (they're display-only, not in actual cart)
        if (item.hasAttribute("data-visual-curated-parent")) {
          return;
        }

        const qty = parseInt(item.getAttribute("data-qty"), 10) || 0;
        const parentPrice = parseFloat(item.getAttribute("data-price") || "0");
        optimisticTotal += qty * parentPrice;
      });

      // Apply welcome15off discount if active (15% off)
      if (this.hasWelcome15Off) {
        optimisticTotal = optimisticTotal * 0.85; // 15% off
      }

      cartTotal = optimisticTotal;
    } else {
      // Use actual cart total from Shopify (includes discounts)
      // this.cartTotal is in cents, so convert to dollars
      cartTotal = (this.cartTotal || 0) / 100;
    }

    // Calculate percentage and remaining amount
    const percentage = Math.min((cartTotal / FREE_SHIPPING_GOAL) * 100, 100);
    const remaining = Math.max(FREE_SHIPPING_GOAL - cartTotal, 0);

    // Update ALL progress bar fill widths (main summary and toggle container)
    const progressFills = this.querySelectorAll("[data-progress-fill]");
    progressFills.forEach((progressFill) => {
      progressFill.style.width = `${percentage}%`;
    });

    // Update ALL messages (main summary and toggle container)
    const progressMessages = this.querySelectorAll("[data-progress-message]");
    progressMessages.forEach((progressMessage) => {
      if (cartTotal >= FREE_SHIPPING_GOAL) {
        // Cart is $50 or more
        progressMessage.textContent = "FREE SHIPPING";
      } else if (cartTotal > 43.0) {
        // Cart is between $43.01 and $49.99
        progressMessage.textContent = "Add one more item for free shipping";
      } else {
        // Cart is $43 or less
        progressMessage.textContent = "Free Shipping for all orders of $50+";
      }
    });

    // Update free shipping badge on one-time option
    const oneTimeOption = this.querySelector(
      ".bundle-summary__option--one-time"
    );
    if (oneTimeOption) {
      if (cartTotal >= FREE_SHIPPING_GOAL) {
        oneTimeOption.classList.add("has-free-shipping");
      } else {
        oneTimeOption.classList.remove("has-free-shipping");
      }
    }

    // Also update the counter display
    const count = this.getBundleCount();
    const countDisplays = this.querySelectorAll("[data-bundle-count]");
    countDisplays.forEach((el) => {
      el.textContent = count;
    });

    // Update the pluralization for items
    const pluralDisplays = this.querySelectorAll("[data-bundle-count-plural]");
    pluralDisplays.forEach((el) => {
      if (count === 1) {
        el.textContent = "";
      } else {
        el.textContent = "s";
      }
    });
  }

  getBundleCount(subscriptionEligibleOnly = false) {
    // Always prioritize DOM count for optimistic updates (instant feedback)
    if (this.itemsContainer) {
      const domItems =
        this.itemsContainer.querySelectorAll("[data-bundle-item]");
      let domTotal = 0;
      domItems.forEach((item) => {
        // Skip visual curated parent items (they're display-only, not in actual cart)
        if (item.hasAttribute("data-visual-curated-parent")) {
          return;
        }

        // If counting subscription-eligible only, skip non-eligible items
        if (
          subscriptionEligibleOnly &&
          !item.hasAttribute("data-subscription-eligible")
        ) {
          return;
        }

        const qty = parseInt(item.getAttribute("data-qty"), 10) || 0;
        domTotal += qty;
      });
      // Always return DOM total for instant feedback
      return domTotal;
    }

    // Fallback to cart items
    let total = 0;
    this.cartItems.forEach((item) => {
      total += item.quantity || 0;
    });

    return total;
  }

  async clearAllCartItems() {
    // Save original state for potential rollback
    const originalHTML = this.itemsContainer
      ? this.itemsContainer.innerHTML
      : "";
    const originalCartItems = [...this.cartItems];

    try {
      // OPTIMISTIC UI UPDATE - Clear the DOM immediately for instant feedback
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = "";
      }

      // Update UI immediately to reflect empty state (optimistic - should be $0.00)
      this.evaluateBundleAndUpdate(true);

      // BACKGROUND CART SYNC - Clear cart on server by setting all items to quantity 0
      // Build updates object with all variant IDs set to 0
      const updates = {};
      this.cartItems.forEach((item) => {
        updates[item.variant_id] = 0;
      });

      await this.updateCart({ updates });

      // Reload cart items to sync with empty cart
      await this.loadCartItems();

      // Update UI again after server sync with accurate pricing
      this.evaluateBundleAndUpdate(false);
    } catch (error) {
      console.error("Failed to clear all cart items:", error);

      // ROLLBACK ON FAILURE - Restore original DOM and cart state
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = originalHTML;
      }
      this.cartItems = originalCartItems;

      // Update UI to reflect restored state
      this.evaluateBundleAndUpdate();

      this.showCartError("Failed to clear cart. Please try again.");
      throw error;
    }
  }

  async handleApplyDiscount() {
    const discountInput = this.querySelector("[data-discount-code]");
    const applyButton = this.querySelector("[data-apply-discount]");

    if (!discountInput || !applyButton) return;

    const discountCode = discountInput.value.trim();

    if (!discountCode) {
      this.showCartError("Please enter a discount code");
      return;
    }

    // Set loading state
    applyButton.disabled = true;
    applyButton.classList.add("loading");
    const originalButtonText = applyButton.textContent;
    applyButton.textContent = "Applying...";

    // Clear any existing error message
    this.clearDiscountError();

    try {
      const updatedCart = await this.updateCart({ discount: discountCode });
      // Fetch the full cart to get discount applications (cart/update.js doesn't always return them)
      const cartResponse = await fetch("/cart.js");
      const fullCart = await cartResponse.json();

      // Check if the discount was successfully applied
      // Shopify can use different properties: cart_level_discount_applications or discount_applications
      let discountApplied = false;
      let discountIsApplicable = true; // Assume applicable unless proven otherwise

      const discountApplications =
        fullCart.cart_level_discount_applications ||
        fullCart.discount_applications ||
        [];

      if (
        Array.isArray(discountApplications) &&
        discountApplications.length > 0
      ) {
        // Find the discount we just tried to apply
        const appliedDiscount = discountApplications.find(
          (discount) =>
            discount.title.toLowerCase() === discountCode.toLowerCase()
        );

        if (appliedDiscount) {
          discountApplied = true;
          // Check if the discount has an "applicable" property
          // If it exists and is false, the discount is not valid for this cart
          if (appliedDiscount.hasOwnProperty("applicable")) {
            discountIsApplicable = appliedDiscount.applicable;
          }
        }
      }

      // Also check if cart has a discount code attribute (Shopify sometimes stores it here)
      if (!discountApplied && fullCart.discount_code) {
        if (
          fullCart.discount_code.toLowerCase() === discountCode.toLowerCase()
        ) {
          discountApplied = true;
        }
      }

      if (!discountApplied || !discountIsApplicable) {
        // Discount was not applied or is not applicable - show error
        this.showDiscountError(
          "This discount code is not valid for your cart."
        );
      } else {
        // Discount was successfully applied
        // If summary is active, add parameter before reload to persist open state
        if (
          this.bundleSummary &&
          this.bundleSummary.hasAttribute("data-active")
        ) {
          this.addSummaryActiveParam();
        }

        // Reload the page to show updated discount badges and pricing
        // The cart will be updated to match the selected selling plan on page load
        window.location.reload();
      }
    } catch (error) {
      console.error("Error applying discount code:", error);
      this.showDiscountError(
        "Failed to apply discount code. Please check the code and try again."
      );
    } finally {
      // Reset button state
      applyButton.disabled = false;
      applyButton.classList.remove("loading");
      applyButton.textContent = originalButtonText;
    }
  }

  async handleRemoveDiscount(discountCodeToRemove) {
    if (!discountCodeToRemove) return;

    try {
      // Fetch current cart to get all discount codes
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Remove the specific discount code by updating with an empty string
      // Shopify's cart/update.js will clear the discount when passed an empty string
      await this.updateCart({ discount: "" });

      // Reload the page to refresh the cart and discount badges
      window.location.reload();
    } catch (error) {
      console.error("Error removing discount code:", error);
      this.showCartError("Failed to remove discount code. Please try again.");
    }
  }

  handleDirectQuantityInput(el) {
    const input = el.querySelector("input[type='number']");
    const inputParent = input.closest("[data-qty-selector]");
    const variantId = inputParent?.getAttribute("data-variant");

    if (!variantId) return;

    // Check if this is a visual curated parent item (display only, not in actual cart)
    const bundleItem = el.closest("[data-bundle-item]");
    if (bundleItem?.hasAttribute("data-visual-curated-parent")) {
      // Visual parent items should not trigger cart updates
      return;
    }

    // Get the line item key (unique identifier) from the bundle item
    const lineKey = bundleItem?.getAttribute("data-line");

    // Get the new quantity from the input
    let newQty = parseInt(input.value, 10) || 0;

    // Don't allow negative quantities
    if (newQty < 0) {
      newQty = 0;
      input.value = 0;
    }

    // Find related elements for this variant
    const productGridItemActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );

    let card = el.closest("[data-bundle-item]");
    let isCardInput = true;

    if (!card) {
      card = this.querySelector(
        `[data-bundle-item][data-variant="${variantId}"]`
      );
      if (card) {
        isCardInput = false;
      }
    }

    // Handle removal if quantity is 0
    if (newQty <= 0) {
      this.removeItemOptimistically(variantId, card, productGridItemActions);
    } else if (card) {
      // Update quantity optimistically
      this.updateQuantityOptimistically(
        variantId,
        newQty,
        input,
        card,
        productGridItemActions,
        isCardInput
      );
    }

    // Store the pending quantity for server sync
    const updateKey = lineKey || variantId;
    const existingPendingUpdate = this.pendingQuantityUpdates.get(updateKey);
    this.pendingQuantityUpdates.set(updateKey, {
      quantity: newQty,
      el: el,
      isNewItem: existingPendingUpdate?.isNewItem || false,
      variantId: variantId,
      lineKey: lineKey,
    });

    // Update UI state immediately with optimistic pricing
    this.evaluateBundleAndUpdate(true);

    // Clear the global debounce timer
    if (this.globalQuantityDebounceTimer) {
      clearTimeout(this.globalQuantityDebounceTimer);
    }

    // Set new global timer - sync ALL pending changes after 500ms of no activity
    // This batches multiple product changes into a single API call
    this.globalQuantityDebounceTimer = setTimeout(() => {
      this.syncAllQuantitiesWithServer();
    }, 500);
  }

  handleQtyChange(el, action) {
    const input = el.querySelector("input[type='number']");
    const inputParent = input.closest("[data-qty-selector]");
    const variantId = inputParent?.getAttribute("data-variant");

    if (!variantId) return;

    // Check if this is a visual curated parent item (display only, not in actual cart)
    const bundleItem = el.closest("[data-bundle-item]");
    if (bundleItem?.hasAttribute("data-visual-curated-parent")) {
      // Visual parent items should not trigger cart updates
      // They are just for display purposes
      return;
    }

    // Get the line item key (unique identifier) from the bundle item
    const lineKey = bundleItem?.getAttribute("data-line");

    // Use debounced quantity change system
    // Pass both lineKey (unique) and variantId (for grid sync)
    this.debouncedQuantityChange(variantId, action, el, lineKey);
  }

  async handleRemoveItem(removeButton) {
    const card = removeButton.closest("[data-bundle-item]");
    if (!card) return;

    // Check if this is a visual curated parent item (display only, not in actual cart)
    if (card.hasAttribute("data-visual-curated-parent")) {
      // Visual parent items should not trigger cart updates
      return;
    }

    const variantId = card.getAttribute("data-variant");
    const lineKey = card.getAttribute("data-line");
    if (!variantId) return;

    // Find all related elements for this variant
    const productGridItemActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );

    // Set loading state on the remove button
    removeButton.disabled = true;
    removeButton.style.opacity = "0.5";

    try {
      // Optimistically remove from UI
      this.removeItemOptimistically(variantId, card, productGridItemActions);

      // Update UI with optimistic pricing
      this.evaluateBundleAndUpdate(true);

      // Remove from cart - use lineKey if available for unique identification
      // Pass variantId as fallback for fresh lookup
      await this.removeCartItem(lineKey || variantId, variantId);

      // Reload cart and update UI with accurate pricing
      await this.loadCartItems();
      this.evaluateBundleAndUpdate(false);
    } catch (error) {
      console.error("Failed to remove item:", error);

      // Show error to user
      this.showCartError("Failed to remove item. Please try again.");

      // Re-enable button on error (item wasn't removed)
      removeButton.disabled = false;
      removeButton.style.opacity = "";
    }
  }

  debouncedQuantityChange(variantId, action, el, lineKey = null) {
    console.log(`[debouncedQuantityChange] Called with variantId: ${variantId}, action: ${action}, lineKey: ${lineKey}`);

    // Get current quantity - use pending quantity if it exists (for rapid clicks)
    const input = el.querySelector("input[type='number']");

    // For pending updates, use lineKey if available, otherwise fall back to variantId
    const updateKey = lineKey || variantId;
    const pendingUpdate = this.pendingQuantityUpdates.get(updateKey);
    let currentQty = pendingUpdate
      ? pendingUpdate.quantity
      : parseInt(input.value, 10) || 0;

    // Calculate new quantity based on action
    let newQty = currentQty;
    if (action === "minus") {
      newQty -= 1;
    } else if (action === "plus") {
      newQty += 1;
    }

    // Don't allow negative quantities
    if (newQty < 0) {
      newQty = 0;
    }

    // OPTIMISTIC UI UPDATE - Update immediately without waiting
    const productGridItemActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );

    let card = el.closest("[data-bundle-item]");
    let isCardInput = true;

    if (!card) {
      card = this.querySelector(
        `[data-bundle-item][data-variant="${variantId}"]`
      );
      if (card) {
        isCardInput = false;
      }
    }

    // If clicking grid plus button when item doesn't exist, we need to add it first
    if (!card && newQty > 0 && currentQty === 0) {
      // This is a grid button click to add a new item
      // Get product data from DOM to create the bundle card
      const productData = this.getProductDataFromDOM(variantId);
      if (!productData) {
        console.error(`Product data not found for variant ${variantId}`);
        return;
      }

      // Create the bundle card optimistically
      const itemHTML = this.generateItemHTML({
        line: Date.now(),
        variantId: variantId,
        title: productData.title,
        image: productData.image,
        currentQuantity: newQty,
        price: productData.price,
        sellingPlan: productData.selling_plan,
        subscriptionEligible: productData.subscription_eligible !== false,
      });

      this.itemsContainer.insertAdjacentHTML("afterbegin", itemHTML);

      // Get the newly created card (first child since we prepended)
      const newCard = this.itemsContainer.querySelector(
        `[data-bundle-item][data-variant="${variantId}"]`
      );

      // Use updateQuantityOptimistically for consistent behavior
      this.updateQuantityOptimistically(
        variantId,
        newQty,
        input,
        newCard,
        productGridItemActions,
        false // isCardInput = false because we clicked grid button
      );

      // Store pending update for server sync (will handle adding new item)
      // For new items, we use variantId as the key since there's no lineKey yet
      this.pendingQuantityUpdates.set(variantId, {
        quantity: newQty,
        el: el,
        isNewItem: true,
        variantId: variantId,
      });

      // Update UI to show the new item with optimistic pricing - do this AFTER setting pending update
      this.evaluateBundleAndUpdate(true);

      // Use global debounce timer to batch all changes
      if (this.globalQuantityDebounceTimer) {
        clearTimeout(this.globalQuantityDebounceTimer);
      }

      this.globalQuantityDebounceTimer = setTimeout(() => {
        this.syncAllQuantitiesWithServer();
      }, 500);

      return;
    }

    // Use updateQuantityOptimistically for ALL updates (not just card updates)
    // This ensures grid and card inputs are ALWAYS updated together
    if (newQty <= 0) {
      // Optimistically remove the item from DOM
      this.removeItemOptimistically(variantId, card, productGridItemActions);
    } else if (card) {
      // Optimistically update quantity using the unified method
      this.updateQuantityOptimistically(
        variantId,
        newQty,
        input,
        card,
        productGridItemActions,
        isCardInput
      );
    }

    // Store the pending quantity for server sync BEFORE calling evaluateBundleAndUpdate
    // This ensures the pending update check will protect our optimistic values
    const existingPendingUpdate = this.pendingQuantityUpdates.get(updateKey);
    this.pendingQuantityUpdates.set(updateKey, {
      quantity: newQty,
      el: el,
      isNewItem: existingPendingUpdate?.isNewItem || false,
      variantId: variantId,
      lineKey: lineKey,
    });

    // Update UI state immediately with optimistic pricing - called AFTER setting pending update
    this.evaluateBundleAndUpdate(true);

    // Clear the global debounce timer
    if (this.globalQuantityDebounceTimer) {
      clearTimeout(this.globalQuantityDebounceTimer);
    }

    // Set new global timer - sync ALL pending changes after 500ms of no activity
    // This batches multiple product changes into a single API call
    this.globalQuantityDebounceTimer = setTimeout(() => {
      this.syncAllQuantitiesWithServer();
    }, 500);
  }

  async syncAllQuantitiesWithServer() {
    // Process ALL pending quantity updates in a single bulk operation
    if (this.pendingQuantityUpdates.size === 0) return;

    // Collect all updates into a single cart/update.js payload
    const updates = {};
    const pendingUpdatesSnapshot = new Map(this.pendingQuantityUpdates);

    // Clear pending updates immediately to prevent double-processing
    this.pendingQuantityUpdates.clear();
    this.updateCheckoutButtonState();

    try {
      // Build the updates object with line keys or variant IDs
      for (const [updateKey, updateData] of pendingUpdatesSnapshot) {
        const { quantity, variantId, lineKey } = updateData;

        // Get fresh line key from cart for accuracy
        const freshCartItem = this.cartItems.find(
          (item) => item.variant_id.toString() === variantId.toString()
        );

        if (freshCartItem) {
          // Use the cart item's key for the update
          updates[freshCartItem.key] = quantity;
        } else if (quantity > 0) {
          // Item doesn't exist yet - we'll need to add it separately
          // For now, skip it in the bulk update
        }
      }

      // If we have updates to make, send them in one bulk request
      if (Object.keys(updates).length > 0) {
        await this.updateCart({ updates });
      }

      // Reload cart once to get fresh data
      await this.loadCartItems();
      this.updateDOMLineKeys();

      // Update UI with accurate pricing
      this.evaluateBundleAndUpdate(false);

      // Handle any items that need to be added (not in cart yet)
      for (const [updateKey, updateData] of pendingUpdatesSnapshot) {
        const { quantity, variantId } = updateData;

        if (quantity > 0 && !this.cartItems.find(item => item.variant_id.toString() === variantId.toString())) {
          // Item needs to be added
          const productData = this.getProductDataFromDOM(variantId);
          if (productData) {
            // addSingleItemToCart now handles cart reload internally before re-enabling controls
            await this.addSingleItemToCart(
              variantId,
              quantity,
              productData.properties || {},
              productData.selling_plan || null
            );
          }
        }
      }

      // Final UI update
      this.evaluateBundleAndUpdate(false);

    } catch (error) {
      console.error("[syncAllQuantities] Failed to sync quantities:", error);
      // Reload cart to get accurate state
      await this.loadCartItems();
      this.evaluateBundleAndUpdate(false);
    }
  }

  async syncQuantityWithServer(updateKey, variantId) {
    // Get the pending quantity update for this item
    const pendingUpdate = this.pendingQuantityUpdates.get(updateKey);
    if (!pendingUpdate) return;

    const { quantity, el, isNewItem, lineKey } = pendingUpdate;

    // Remove from pending updates
    this.pendingQuantityUpdates.delete(updateKey);
    this.quantityDebounceTimers.delete(updateKey);

    // Update checkout button state since we removed a pending update
    this.updateCheckoutButtonState();

    // Store original state for potential rollback
    const input = el.querySelector("input[type='number']");
    const originalQty = parseInt(input.value, 10) || 0;

    const productGridItemActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );

    let card = el.closest("[data-bundle-item]");
    let isCardInput = true;

    if (!card) {
      card = this.querySelector(
        `[data-bundle-item][data-variant="${variantId}"]`
      );
      if (card) {
        isCardInput = false;
      }
    }

    const originalProduct = this.products[variantId]
      ? { ...this.products[variantId] }
      : null;
    const originalProductsState = { ...this.products };

    // BACKGROUND CART SYNC - Handle cart operations
    try {
      let updatedCart = null;

      // Always get the freshest lineKey from cartItems before making any cart API call
      // This prevents issues when DOM hasn't been updated yet with real Shopify line keys
      const freshCartItem = this.cartItems.find(
        (item) => item.variant_id.toString() === variantId.toString()
      );
      const freshLineKey = freshCartItem ? freshCartItem.key : (lineKey || variantId);

      // Check if this is an item that hasn't been added to cart yet
      // This can happen on slow connections when debounce fires before cart add completes
      const itemExistsInCart = this.cartItems.some(
        (item) => item.variant_id.toString() === variantId.toString()
      );

      if (quantity <= 0) {
        // removeCartItem will handle the case where item is already removed
        // Pass variantId so it can look up fresh line key inside the queued operation
        await this.removeCartItem(freshLineKey, variantId);

        // Need to load cart for remove operations
        await this.loadCartItems();
        // Update DOM line keys after cart is reloaded
        this.updateDOMLineKeys();
      } else if (isNewItem) {
        // This is a new item that needs to be added to the cart
        // Get product data from DOM to add to cart
        const productData = this.getProductDataFromDOM(variantId);
        if (!productData) {
          throw new Error(`Product data not found for variant ${variantId}`);
        }

        // Add item to cart with properties
        await this.addSingleItemToCart(
          variantId,
          quantity,
          productData.properties || {},
          productData.selling_plan || null
        );
        // Need to load cart for add operations
        await this.loadCartItems();
        // Update DOM line keys after cart is reloaded
        this.updateDOMLineKeys();
      } else if (!itemExistsInCart) {
        // Item doesn't exist in cart yet - this means the initial add hasn't completed
        // Refresh cart data to get the latest state, then retry the update
        console.log(`Item ${variantId} not found in cart yet, refreshing...`);
        await this.loadCartItems();

        // After refresh, check again
        const refreshedCartItem = this.cartItems.find(
          (item) => item.variant_id.toString() === variantId.toString()
        );

        if (refreshedCartItem) {
          // Item now exists, update its quantity
          updatedCart = await this.updateCartItemQuantity(
            refreshedCartItem.key,
            quantity
          );
          if (updatedCart) {
            this.processCartData(updatedCart);
            this.updateDOMLineKeys();
          }
        } else {
          // Still not in cart - it might be in the queue to be added
          // Add it with the final quantity to avoid duplicate operations
          const productData = this.getProductDataFromDOM(variantId);
          if (productData) {
            await this.addSingleItemToCart(
              variantId,
              quantity,
              productData.properties || {},
              productData.selling_plan || null
            );
            await this.loadCartItems();
            this.updateDOMLineKeys();
          }
        }
      } else {
        // Update existing cart item quantity - this returns the full updated cart
        // Use fresh lineKey for accurate cart item identification
        updatedCart = await this.updateCartItemQuantity(
          freshLineKey,
          quantity
        );
        // Process the cart data returned from the API (no need for separate /cart.js call)
        if (updatedCart) {
          this.processCartData(updatedCart);
          // Update DOM line keys to match the new cart state
          this.updateDOMLineKeys();
        }
      }

      // Always update UI to keep grid and bundle summary in sync with accurate Shopify pricing
      // This is safe because we deleted the pending update above (line 1835)
      // So there's no risk of overwriting optimistic changes
      this.evaluateBundleAndUpdate(false);
    } catch (error) {
      console.error("Failed to sync quantity with server:", error);

      // ROLLBACK ON FAILURE - Restore original state
      this.rollbackQuantityChange(
        originalProductsState,
        originalProduct,
        variantId,
        originalQty,
        input,
        card,
        productGridItemActions,
        isCardInput
      );

      // Show user notification about the failure
      this.showCartError("Failed to update item quantity. Please try again.");
    }
  }

  removeItemOptimistically(variantId, card, productGridItemActions) {
    // No longer using products object - working directly with cart

    // Remove card from UI
    if (card) {
      card.remove();
    }

    // Hide grid item action controls - ensure we find them if not passed
    const gridActions =
      productGridItemActions && productGridItemActions.length > 0
        ? productGridItemActions
        : document.querySelectorAll(`[data-grid-item-actions="${variantId}"]`);

    if (gridActions && gridActions.length > 0) {
      gridActions.forEach((productGridItemAction) => {
        productGridItemAction.classList.remove("controls-active");
        const gridItemInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (gridItemInput) {
          gridItemInput.value = "";
        }
      });
    }

    // Update clear button visibility based on remaining DOM items
    const remainingItems =
      this.itemsContainer.querySelectorAll("[data-bundle-item]");
    if (remainingItems.length === 0) {
      this.clearBundleButton?.forEach((button) => {
        button.setAttribute("hidden", "");
      });
    }

    // Save state
  }

  updateQuantityOptimistically(
    variantId,
    newQty,
    input,
    card,
    productGridItemActions,
    isCardInput
  ) {
    // ALWAYS update the input that was clicked
    input.value = newQty;

    // ALWAYS sync both grid and card inputs
    // Update grid item inputs
    productGridItemActions.forEach((productGridItemAction) => {
      productGridItemAction.classList.add("controls-active");
      const gridItemInput = productGridItemAction.querySelector(
        "input.bundle-qty-input"
      );
      if (gridItemInput) {
        gridItemInput.value = newQty;
      }
    });

    // Update card input if it exists
    if (card) {
      const cardInput = card.querySelector("input.bundle-qty-input");
      if (cardInput) {
        cardInput.value = newQty;
      }
      // Update card attributes
      card.setAttribute("data-qty", newQty);
    }

    // No longer using products object - working directly with cart and DOM
  }

  rollbackQuantityChange(
    originalProductsState,
    originalProduct,
    variantId,
    originalQty,
    input,
    card,
    productGridItemActions,
    isCardInput
  ) {
    // Restore products state
    this.products = originalProductsState;

    // Restore input values
    input.value = originalQty;

    // Restore grid controls
    if (originalProduct) {
      productGridItemActions.forEach((productGridItemAction) => {
        productGridItemAction.classList.add("controls-active");
        const gridItemInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (gridItemInput) {
          gridItemInput.value = originalQty;
        }
      });
    } else {
      productGridItemActions.forEach((productGridItemAction) => {
        productGridItemAction.classList.remove("controls-active");
        const gridItemInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (gridItemInput) {
          gridItemInput.value = "";
        }
      });
    }

    // Restore card attributes
    if (card && originalProduct) {
      card.setAttribute("data-qty", originalQty);
    }

    // If item was removed optimistically but cart update failed, restore it
    if (!card && originalProduct) {
      // Get subscription eligibility from DOM
      const productData = this.getProductDataFromDOM(variantId);
      const subscriptionEligible = productData
        ? productData.subscription_eligible !== false
        : true;

      const itemHTML = this.generateItemHTML({
        ...originalProduct,
        subscriptionEligible: subscriptionEligible,
      });
      this.itemsContainer.insertAdjacentHTML("afterbegin", itemHTML);
    }

    // Save rolled back state
    this.evaluateBundleAndUpdate();
  }

  rollbackAddToBundle(
    originalProductsState,
    originalProduct,
    variantId,
    existingItem,
    addedItemElement,
    productGridItemActions
  ) {
    // Restore products state
    this.products = originalProductsState;

    if (originalProduct) {
      // Item already existed, restore its original quantity
      if (existingItem) {
        const input = existingItem.querySelector("input.bundle-qty-input");
        input.value = originalProduct.currentQuantity;
        existingItem.setAttribute("data-qty", originalProduct.currentQuantity);
      }

      // Restore grid controls to original quantity
      productGridItemActions.forEach((productGridItemAction) => {
        const qtyInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (qtyInput) {
          qtyInput.value = originalProduct.currentQuantity;
        }
      });
    } else {
      // Item was newly added, remove it completely
      if (addedItemElement) {
        addedItemElement.remove();
      }

      // Remove grid controls
      productGridItemActions.forEach((productGridItemAction) => {
        productGridItemAction.classList.remove("controls-active");
        const qtyInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (qtyInput) {
          qtyInput.value = "";
        }
      });
    }

    // Save rolled back state
    this.evaluateBundleAndUpdate();
  }

  showCartError(message) {
    // Simple error notification - you might want to implement a more sophisticated notification system
    console.error(message);
    // Optionally implement a toast notification or other user feedback here
  }

  showCartSuccess(message) {
    // Simple success notification - you might want to implement a more sophisticated notification system
    // Optionally implement a toast notification or other user feedback here
  }

  showDiscountError(message) {
    // Find or create discount error container
    const discountContainer = this.querySelector(".bundle-summary__discount");
    if (!discountContainer) return;

    // Remove any existing error message
    this.clearDiscountError();

    // Create error message element
    const errorElement = document.createElement("div");
    errorElement.className = "discount-error-message";
    errorElement.setAttribute("data-discount-error", "");
    errorElement.style.color = "#d32f2f";
    errorElement.style.fontSize = "13px";
    errorElement.style.marginTop = "8px";
    errorElement.style.marginBottom = "8px";
    errorElement.style.fontFamily = "sans-serif";
    errorElement.textContent = message;

    // Insert error message after the discount container
    discountContainer.insertAdjacentElement("afterend", errorElement);
  }

  clearDiscountError() {
    // Remove any existing error messages
    const errorMessages = this.querySelectorAll("[data-discount-error]");
    errorMessages.forEach((error) => error.remove());
  }

  async handleAddToBundle(event) {
    if (!event.target.matches(".btn")) return;
    const button = event.target;

    button.classList.add("loading");
    button.disabled = true;
    button.textContent = "Adding...";

    const prouductJson = button
      .closest("[data-bundle-grid-footer]")
      .querySelector("[data-bundle-product-json]");

    const productData = JSON.parse(prouductJson.textContent || "{}");

    // Always attach _bundle_builder_id
    if (!productData.properties) {
      productData.properties = {};
    }
    productData.properties._bundle_builder_id = this.bundleId;

    const variantId = productData.id;
    const title = productData.title;
    const image = productData.image;
    const price = parseFloat(productData.price) || 0;
    const sellingPlan = productData.selling_plan;
    const properties = productData.properties || {};
    const line = Date.now();
    const quantity = 1;

    if (!variantId || !title || !image) {
      console.error("Missing required product data.");
      button.classList.remove("loading");
      button.disabled = false;
      button.textContent = "Add to Bundle";
      return;
    }

    // Store original state for potential rollback (using cart items now)
    const existingCartItem = this.cartItems.find(
      (item) => item.variant_id.toString() === variantId.toString()
    );
    const originalCartItemsState = [...this.cartItems];

    // 1. OPTIMISTIC UI UPDATE - Show item immediately in bundle
    const existingItem = this.itemsContainer.querySelector(
      `[data-variant="${variantId}"]`
    );

    let newQuantity;
    let addedItemElement = null;

    if (existingItem) {
      const input = existingItem.querySelector("input.bundle-qty-input");
      const originalQty = parseInt(input.value, 10) || 0;
      newQuantity = originalQty + 1;
      input.value = newQuantity;
      existingItem.setAttribute("data-qty", newQuantity);
    } else {
      newQuantity = quantity;
      const itemHTML = this.generateItemHTML({
        line,
        variantId,
        title,
        image,
        currentQuantity: newQuantity,
        price,
        sellingPlan,
        subscriptionEligible: productData.subscription_eligible !== false,
      });
      this.itemsContainer.insertAdjacentHTML("afterbegin", itemHTML);
      addedItemElement = this.itemsContainer.querySelector(
        `[data-variant="${variantId}"]:first-child`
      );
    }

    // No longer using products object - working directly with cart

    // Activate the grid item action controls
    const productGridItemActions = this.querySelectorAll(
      `[data-grid-item-actions="${variantId}"]`
    );
    if (productGridItemActions) {
      productGridItemActions.forEach((productGridItemAction) => {
        productGridItemAction.classList.add("controls-active");
        const qtyInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (qtyInput) {
          qtyInput.value = newQuantity;
        }
      });
    }

    // Update UI optimistically (this will show the item immediately with calculated pricing)
    this.evaluateBundleAndUpdate(true);

    // 2. ADD TO CART IN BACKGROUND - With rollback on failure
    try {
      // Add to cart (returns only the added line item, not full cart)
      await this.addSingleItemToCart(variantId, 1, properties, sellingPlan);
      // Fetch full cart to get updated totals and all items
      await this.loadCartItems();
      // Update DOM line keys to match new cart state (fast - no HTML fetch)
      this.updateDOMLineKeys();
      // Update UI again with accurate Shopify cart pricing
      this.evaluateBundleAndUpdate(false);
    } catch (error) {
      console.error("Failed to add item to cart:", error);

      // ROLLBACK ON FAILURE - Restore original DOM state
      if (addedItemElement) {
        addedItemElement.remove();
      } else if (existingItem) {
        // Restore original quantity
        const input = existingItem.querySelector("input.bundle-qty-input");
        const originalQty = existingCartItem ? existingCartItem.quantity : 0;
        input.value = originalQty;
        existingItem.setAttribute("data-qty", originalQty);
      }

      // Restore grid controls
      productGridItemActions.forEach((productGridItemAction) => {
        const qtyInput = productGridItemAction.querySelector(
          "input.bundle-qty-input"
        );
        if (qtyInput) {
          qtyInput.value = existingCartItem ? existingCartItem.quantity : "";
        }
        if (!existingCartItem) {
          productGridItemAction.classList.remove("controls-active");
        }
      });

      // Show user notification about the failure
      this.showCartError("Failed to add item to bundle. Please try again.");
    }

    button.classList.remove("loading");
    button.disabled = false;
    button.textContent = "Add to Bundle";
  }

  generateItemHTML({
    line,
    variantId,
    title,
    image,
    currentQuantity,
    price,
    sellingPlan,
    subscriptionEligible = true,
  }) {
    const sellingPlanAttrs =
      sellingPlan && sellingPlan.id
        ? `data-selling-plan-id="${sellingPlan.id}" data-selling-plan-name="${sellingPlan.name}"`
        : "";

    const subscriptionEligibleAttr = subscriptionEligible
      ? 'data-subscription-eligible="true"'
      : "";
    const subscriptionIneligibleLabel = !subscriptionEligible
      ? '<div class="bundle-product-subscription-eligible">Not Eligible for Subscription</div>'
      : "";

    return `
    <div
      class="bundle-product-card"
      data-bundle-item
      data-line="${line || ""}"
      data-qty="${currentQuantity}"
      data-price="${price}"
      data-variant="${variantId}"
      ${sellingPlanAttrs}
      ${subscriptionEligibleAttr}
    >
      <img src="${image}" alt="${title}" class="bundle-product-image" />
      <div class="bundle-product-info">
        <div class="bundle-product-title">${title}</div>
        ${subscriptionIneligibleLabel}
      </div>
      <div class="quantity-selector" data-qty-selector data-variant="${variantId}">
        <button class="qty-btn" data-bundle-qty-control="minus">−</button>
        <input type="number" value="${currentQuantity}" class="bundle-qty-input" />
        <button class="qty-btn" data-bundle-qty-control="plus">+</button>
      </div>
    </div>
  `;
  }

  updateTotal(useOptimisticPricing = false) {
    const items = this.querySelectorAll("[data-bundle-item]");
    let oneTimePurchaseTotal = 0; // Total for one-time purchases (parent prices)
    let subscriptionTotal = 0; // Total for subscriptions (child item prices)

    items.forEach((item) => {
      // Skip visual curated parent items (they're display-only, not in actual cart)
      if (item.hasAttribute("data-visual-curated-parent")) {
        return;
      }

      const qty = parseInt(item.getAttribute("data-qty"), 10) || 0;
      const variantId = item.getAttribute("data-variant");
      const parentPrice = parseFloat(item.getAttribute("data-price") || "0");
      const isSubscriptionEligible = item.hasAttribute(
        "data-subscription-eligible"
      );

      // Calculate line total using data-price attribute for both one-time and subscription
      const lineTotal = qty * parentPrice;
      oneTimePurchaseTotal += lineTotal;

      // Add to subscription total - only apply discount to subscription-eligible items when subscription bundle is met
      if (
        isSubscriptionEligible &&
        this.isSubscriptionBundleMet &&
        this.discountAmount > 0
      ) {
        subscriptionTotal +=
          lineTotal - (lineTotal * this.discountAmount) / 100;
      } else {
        subscriptionTotal += lineTotal;
      }
    });

    // OPTIMISTIC: Always use DOM-calculated total for instant feedback
    // This ensures zero latency for all price updates
    let optimisticTotal = oneTimePurchaseTotal;
    let optimisticSubscriptionTotal = subscriptionTotal;

    // Calculate if there's a discount_code type discount from server
    // Only show strikethrough pricing if there's a discount code applied
    const hasDiscountCode =
      this.hasDiscountCode && this.cartTotalDiscount > 0 && this.cartTotal > 0;

    // For curated bundles in subscription mode, we need to calculate the discount code effect
    // based on child item prices (subscriptionTotal), not parent item prices (server total)
    let finalSubscriptionPrice = subscriptionTotal;
    let finalOneTimePrice = oneTimePurchaseTotal;

    if (useOptimisticPricing) {
      // OPTIMISTIC PRICING: Calculate discounts client-side for instant feedback

      // Apply welcome15off discount if active (15% off entire cart)
      if (this.hasWelcome15Off) {
        optimisticTotal = oneTimePurchaseTotal * 0.85; // 15% off
        optimisticSubscriptionTotal = subscriptionTotal * 0.85; // 15% off
      }

      finalSubscriptionPrice = optimisticSubscriptionTotal;
      finalOneTimePrice = optimisticTotal;
    } else if (hasDiscountCode) {
      // SERVER PRICING: Use accurate Shopify pricing
      // Calculate the discount code percentage from server data
      // discountCodeAmount is in cents, cartOriginalTotal is in cents
      const discountCodePercentage =
        this.cartOriginalTotal > 0
          ? (this.discountCodeAmount / this.cartOriginalTotal) * 100
          : 0;

      // Apply the same percentage to our subscriptionTotal (which is based on child items)
      if (discountCodePercentage > 0) {
        const discountCodeAmount =
          (subscriptionTotal * discountCodePercentage) / 100;
        finalSubscriptionPrice = subscriptionTotal - discountCodeAmount;

        // Also apply to one-time total
        const oneTimeDiscountAmount =
          (oneTimePurchaseTotal * discountCodePercentage) / 100;
        finalOneTimePrice = oneTimePurchaseTotal - oneTimeDiscountAmount;
      } else {
        // Fallback: use server's cart total if we can't calculate percentage
        finalSubscriptionPrice = this.cartTotal / 100;
        finalOneTimePrice = this.cartTotal / 100;
      }
    }

    // Update the subscription price displays
    this.querySelectorAll("[data-bundle-total-with-discount]").forEach((el) => {
      // Check if this element is inside the checkout button
      const isInsideCheckoutButton = el.closest(
        "[data-checkout-with-subscription]"
      );

      if (isInsideCheckoutButton) {
        // Inside checkout button: Show optimistic or actual cart total
        let displayPrice;
        let showStrikethrough = false;

        if (useOptimisticPricing) {
          // Use calculated subscription total (optimistic)
          displayPrice = finalSubscriptionPrice.toFixed(2);
          // Show strike-through if we applied a discount optimistically
          showStrikethrough = this.hasWelcome15Off;
        } else {
          // Use Shopify cart total (accurate)
          displayPrice = (this.cartTotal / 100).toFixed(2);
          // Show strike-through if there's a discount code
          showStrikethrough = hasDiscountCode;
        }

        if (showStrikethrough) {
          el.innerHTML = `<span style="text-decoration: line-through; opacity: 0.6;">$${subscriptionTotal.toFixed(2)}</span> $${displayPrice}`;
        } else {
          el.innerHTML = `$${displayPrice}`;
        }
      } else {
        // Outside checkout button (option card): Clear the price
        el.innerHTML = "";
      }
    });

    // Display the one-time total (already calculated above in finalOneTimePrice)
    // Use optimistic or server pricing depending on useOptimisticPricing flag
    let displayOneTimePrice;
    let showOneTimeStrikethrough = false;

    if (useOptimisticPricing) {
      displayOneTimePrice = finalOneTimePrice.toFixed(2);
      // Show strike-through if we applied a discount optimistically
      showOneTimeStrikethrough = this.hasWelcome15Off;
    } else if (hasDiscountCode) {
      // Use Shopify cart total (accurate) when discount code is applied
      displayOneTimePrice = (this.cartTotal / 100).toFixed(2);
      showOneTimeStrikethrough = true;
    } else {
      // No discount, use server total
      displayOneTimePrice = (this.cartTotal / 100).toFixed(2);
    }

    // Update the one-time option price (clear it - no longer showing)
    this.querySelectorAll("[data-bundle-total-original]").forEach((el) => {
      el.innerHTML = "";
    });

    // Update the checkout button price (shows strikethrough when discount is applied)
    this.querySelectorAll("[data-bundle-total]").forEach((el) => {
      if (showOneTimeStrikethrough) {
        // Show strikethrough with original price + discounted price
        el.innerHTML = `<span style="text-decoration: line-through; opacity: 0.6;">$${oneTimePurchaseTotal.toFixed(2)}</span> $${displayOneTimePrice}`;
      } else {
        // Show price without strike-through
        el.innerHTML = `$${displayOneTimePrice}`;
      }
    });
  }

  updateItemPrices() {
    // Update individual item prices based on the current selling plan and discount tier
    const items = this.querySelectorAll("[data-bundle-item]");
    const isSubscription = this.sellingPlan === "subscription";
    const isBundleMet = this.isBundleMet;
    const discountAmount = this.discountAmount;

    items.forEach((item) => {
      // Get ALL price containers (mobile and desktop)
      const priceContainers = item.querySelectorAll("[data-price]");
      if (priceContainers.length === 0) return; // Skip if this item doesn't have any price containers

      const qty = parseInt(item.getAttribute("data-qty"), 10) || 0;
      const variantId = item.getAttribute("data-variant");
      const parentPrice = parseFloat(item.getAttribute("data-price") || "0");
      const isSubscriptionEligible = item.hasAttribute(
        "data-subscription-eligible"
      );

      // Calculate line price (same for both one-time and subscription)
      const linePrice = qty * parentPrice;

      // Update all price containers (both mobile and desktop)
      priceContainers.forEach((priceContainer) => {
        const subscriptionPriceEl = priceContainer.querySelector(
          "[data-subscription-price]"
        );
        const oneTimePriceEl = priceContainer.querySelector(
          "[data-one-time-price]"
        );

        if (!subscriptionPriceEl || !oneTimePriceEl) return; // Skip if price elements don't exist

        // Always update one-time price
        oneTimePriceEl.textContent = this.formatMoney(linePrice);

        // Calculate and update subscription price - only for eligible items when subscription bundle is met
        if (
          isSubscription &&
          this.isSubscriptionBundleMet &&
          isSubscriptionEligible &&
          discountAmount > 0
        ) {
          // Calculate discounted price for subscription
          const discountedLinePrice =
            linePrice - (linePrice * discountAmount) / 100;
          subscriptionPriceEl.textContent =
            this.formatMoney(discountedLinePrice);
        } else {
          // When no discount or not eligible, keep subscription price empty or same as one-time
          subscriptionPriceEl.textContent = "";
        }
      });
    });
  }

  formatMoney(cents) {
    // Simple money formatting - you can enhance this based on your theme's money format
    const dollars = (cents / 1).toFixed(2);
    return `$${dollars}`;
  }
}

customElements.define("bundle-builder", BundleBuilder);

window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    document.querySelectorAll("bundle-builder").forEach((el) => {
      if (typeof el.init === "function" && !el.initialized) {
        el.init();
      }
    });
  }
});

// Bundle filters navigation
function handleBundleFilterChange(event) {
  const url = event.target.value;
  if (!url) return;

  (async () => {
    try {
      // Add section-rendering parameter to the URL
      const sectionUrl = url.includes('?')
        ? `${url}&sections=build-your-bundle`
        : `${url}?sections=build-your-bundle`;

      // Fetch the section HTML
      const response = await fetch(sectionUrl);
      if (!response.ok) throw new Error('Failed to fetch section');

      const data = await response.json();
      const sectionHtml = data['build-your-bundle'];

      if (!sectionHtml) throw new Error('Section HTML not found');

      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHtml;

      // Find the products area (filters + grid) in the new HTML
      const newProductsArea = tempDiv.querySelector('[data-bundle-products-area]');
      const currentProductsArea = document.querySelector('[data-bundle-products-area]');

      if (newProductsArea && currentProductsArea) {
        // Replace only the products area (filters + grid), not the entire bundle-builder
        currentProductsArea.replaceWith(newProductsArea);

        // Update the URL without reloading the page
        window.history.pushState({}, '', url);

        // Re-initialize the filter event listener
        initBundleFilters();

        // Update grid controls to show quantity selectors for items in cart
        const bundleBuilder = document.querySelector('bundle-builder');
        if (bundleBuilder) {
          bundleBuilder.evaluateBundleAndUpdate(false);
        }
      }
    } catch (error) {
      console.error('Error loading bundle filter:', error);
      // Fallback to full page navigation if section rendering fails
      window.location.href = url;
    }
  })();
}

function initBundleFilters() {
  const bundleFilters = document.querySelector("#bundle-filters");
  if (!bundleFilters) return;

  // Remove existing listener if any to prevent duplicates
  bundleFilters.removeEventListener("change", handleBundleFilterChange);
  // Add the event listener
  bundleFilters.addEventListener("change", handleBundleFilterChange);
}

// Initialize bundle filters when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBundleFilters);
} else {
  initBundleFilters();
}